/**
 * Orchestrator Page — DAG-based Task Orchestration
 * 3 states: Input, Plan Ready, Executing
 * Preact + SVG DAG + CSS Animations
 */

import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { isAdmin, hasActivePlan } from '../stores/auth.store';
import { NoPlanGate } from '../components/NoPlanGate';
import { api } from '../api/client';
import { INTEGRATIONS, fetchIntegrationsCatalog } from '../components/AgentCredentials.js';
import { SkeletonBar, Button, Select, Textarea } from '../components/ui';
import { T } from '../styles/tokens';

// ── Types ──
interface Task {
  id: string;
  title: string;
  description: string;
  task_type: string;
  phase: string | null;
  priority: number;
  depends_on: string;
  assigned_runner_id: string | null;
  assigned_agent_name: string | null;
  status: string;
  output: string | null;
  estimated_duration_min: number;
  started_at: string | null;
  completed_at: string | null;
  // SDD fields
  execution_step?: string;
  research_output?: string | null;
  spec_output?: string | null;
  brains?: Array<{ key: string; emoji: string; label: string; color: string }>;
}

interface Plan {
  id: string;
  title: string;
  original_prompt: string;
  status: string;
  mode?: string;
  total_tasks: number;
  completed_tasks: number;
  failed_tasks: number;
  estimated_duration_min: number;
  actual_duration_min: number | null;
  final_output: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  plan_json?: string;
  category?: string;
  brains?: Array<{ key: string; emoji: string; label: string; color: string }>;
}

interface OrcEvent {
  id: string;
  plan_id: string;
  task_id: string | null;
  event_type: string;
  event_data: string;
  created_at: string;
}

// ── CSS ──
const STYLES = `
@keyframes pulse { 0%,100% { transform:scale(1); } 50% { transform:scale(1.05); } }
@keyframes blink { 0%,100% { opacity:1; } 50% { opacity:0; } }
@keyframes burst { 0% { transform:scale(1); } 50% { transform:scale(1.15); } 100% { transform:scale(1); } }
@keyframes shake { 0%,100% { transform:translateX(0); } 25% { transform:translateX(-3px); } 75% { transform:translateX(3px); } }
@keyframes flowDash { 0% { stroke-dashoffset:20; } 100% { stroke-dashoffset:0; } }
@keyframes fadeInUp { from { opacity:0;transform:translateY(20px); } to { opacity:1;transform:translateY(0); } }
@keyframes slideInRight { from { opacity:0;transform:translateX(30px); } to { opacity:1;transform:translateX(0); } }
@keyframes glowPulse { 0%,100% { box-shadow:0 0 5px ${T.accentBg}; } 50% { box-shadow:0 0 20px ${T.accentBgStrong}; } }
@keyframes popIn { 0% { transform:scale(0); } 70% { transform:scale(1.2); } 100% { transform:scale(1); } }
@keyframes confettiRise { 0% { transform:translateY(0) rotate(0);opacity:1; } 100% { transform:translateY(-200px) rotate(720deg);opacity:0; } }
@keyframes onlinePulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
@keyframes crossfadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
@keyframes expandDown { from { max-height:0; opacity:0; overflow:hidden; } to { max-height:400px; opacity:1; overflow:visible; } }
@keyframes glowSelect { 0%,100% { box-shadow:0 0 10px ${T.accentBg}; } 50% { box-shadow:0 0 25px ${T.accentBgStrong}; } }

.orch-mode-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 12px; }
@media (max-width: 600px) { .orch-mode-grid { grid-template-columns: 1fr; gap: 8px; } }
.orch-mode-card { background: #12121a; border: 1px solid ${T.border}; border-radius: 12px; padding: 20px; cursor: pointer; transition: all 0.3s ease; position: relative; }
.orch-mode-card:hover { border-color: var(--t-white-alpha-15); background: var(--t-white-alpha-2); }
.orch-mode-card.selected { border-color: #7c5cfc; background: ${T.accentBgSubtle}; box-shadow: 0 0 20px ${T.accentBg}; transform: scale(1.02); animation: glowSelect 3s infinite; }
.orch-mode-icon { font-size: 28px; margin-bottom: 8px; }
.orch-mode-name { font-size: 16px; font-weight: 600; color: ${T.text}; margin-bottom: 4px; }
.orch-mode-desc { font-size: 13px; color: ${T.textSecondary}; margin-bottom: 12px; line-height: 1.4; }
.orch-mode-pros { list-style: none; padding: 0; margin: 0 0 8px 0; }
.orch-mode-pros li { font-size: 12px; color: ${T.textSecondary}; padding: 2px 0; }
.orch-mode-pros li::before { margin-right: 6px; }
.orch-mode-cost { font-size: 12px; color: #7c5cfc; font-weight: 500; margin-top: 8px; }
.orch-mode-explain { animation: expandDown 0.4s ease forwards; overflow: hidden; }
.orch-mode-explain-inner { background: #12121a; border: 1px solid ${T.accentBg}; border-radius: 12px; padding: 20px; margin-bottom: 16px; }
.orch-mode-explain h4 { color: ${T.text}; font-size: 15px; margin: 0 0 8px 0; }
.orch-mode-explain p { color: ${T.textSecondary}; font-size: 13px; line-height: 1.6; margin: 0 0 12px 0; }
.orch-mode-explain .ideal { background: ${T.accentBgSubtle}; border-radius: 8px; padding: 12px; margin-top: 12px; }
.orch-mode-explain .ideal strong { color: ${T.purple}; }

.orch-page { padding: 32px; max-width: 1200px; margin: 0 auto; animation: crossfadeIn 0.5s ease; box-sizing: border-box; }
@media (max-width: 600px) { .orch-page { padding: 12px; } }
.orch-title { font-size: 24px; font-weight: 600; color: ${T.text}; margin-bottom: 24px; }
.orch-card { background: #12121a; border: 1px solid ${T.border}; border-radius: 12px; padding: 20px; margin-bottom: 16px; }
.orch-textarea { width: 100%; min-height: 120px; max-height: 300px; background: #1a1a24; border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; color: ${T.text}; font-size: 15px; padding: 14px; resize: vertical; font-family: inherit; outline: none; transition: border-color 0.3s; }
.orch-textarea:focus { border-color: #7c5cfc; }
.orch-textarea::placeholder { color: ${T.textMuted}; }
.orch-btn { height: 40px; padding: 0 24px; border-radius: 8px; border: none; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; gap: 8px; }
.orch-btn-primary { background: #7c5cfc; color: white; }
.orch-btn-primary:hover { background: #6b4feb; }
.orch-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.orch-btn-secondary { background: transparent; border: 1px solid var(--t-white-alpha-15); color: #ccc; }
.orch-btn-secondary:hover { border-color: var(--t-white-alpha-30); }
.orch-btn-danger { background: ${T.danger}; color: white; }
.orch-btn-danger:hover { background: ${T.dangerHover}; }

.orch-status-bar { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; margin-bottom: 16px; color: ${T.textSecondary}; font-size: 14px; }
.orch-status-bar span { display: flex; align-items: center; gap: 6px; }

.orch-task-item { display: flex; align-items: flex-start; gap: 12px; padding: 14px 16px; border-left: 4px solid ${T.textMuted}; border-radius: 0 8px 8px 0; background: var(--t-white-alpha-2); margin-bottom: 8px; animation: slideInRight 0.4s ease; transition: all 0.3s; }
.orch-task-item[data-status="completed"] { border-left-color: ${T.success}; }
.orch-task-item[data-status="running"], .orch-task-item[data-status="sent"] { border-left-color: ${T.warning}; animation: slideInRight 0.4s ease, glowPulse 2s infinite; }
.orch-task-item[data-status="failed"] { border-left-color: ${T.danger}; }

.orch-task-badge { min-width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; background: var(--t-white-alpha-8); color: #ccc; flex-shrink: 0; }
.orch-task-info { flex: 1; }
.orch-task-title { font-size: 14px; font-weight: 500; color: ${T.text}; }
.orch-task-meta { font-size: 12px; color: ${T.textMuted}; margin-top: 4px; display: flex; gap: 12px; flex-wrap: wrap; }
.orch-task-type { background: ${T.accentBg}; color: ${T.purple}; padding: 2px 8px; border-radius: 4px; font-size: 11px; }
.orch-task-agent { background: ${T.border}; color: ${T.textSecondary}; padding: 2px 8px; border-radius: 4px; font-size: 11px; }
.orch-task-deps { color: ${T.textMuted}; font-size: 12px; margin-top: 4px; }

.orch-status-pill { display: inline-flex; align-items: center; gap: 4px; padding: 2px 10px; border-radius: 12px; font-size: 11px; font-weight: 500; }
.orch-status-pill.pending { background: rgba(85,85,85,0.2); color: ${T.textSecondary}; }
.orch-status-pill.running, .orch-status-pill.sent { background: ${T.warningBg}; color: ${T.warning}; }
.orch-status-pill.completed { background: ${T.successBg}; color: ${T.success}; }
.orch-status-pill.failed { background: ${T.dangerBg}; color: ${T.danger}; }
.orch-status-pill.generating { background: ${T.warningBg}; color: ${T.warning}; animation: pulse 1.5s infinite; }
.orch-status-pill.estimated, .orch-status-pill.researched { background: rgba(168,85,247,0.15); color: ${T.purple}; }
.orch-status-pill.ready { background: ${T.primaryBg}; color: ${T.primary}; }
.orch-status-pill.executing { background: ${T.warningBg}; color: ${T.warning}; animation: pulse 1.5s infinite; }
.orch-status-pill.cancelled { background: rgba(107,114,128,0.15); color: #6b7280; }
.orch-pulse-dot { width: 8px; height: 8px; border-radius: 50%; background: ${T.warning}; animation: pulse 1s infinite; flex-shrink: 0; }

.orch-timeline { max-height: 400px; overflow-y: auto; scroll-behavior: smooth; }
.orch-timeline-item { display: flex; gap: 12px; padding: 8px 0; animation: fadeInUp 0.3s ease; border-bottom: 1px solid var(--t-white-alpha-3); }
.orch-timeline-item.flash { background: ${T.accentBgSubtle}; transition: background 1s; }
.orch-timeline-ts { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: ${T.textMuted}; min-width: 70px; flex-shrink: 0; }
.orch-timeline-text { font-size: 13px; color: ${T.textSecondary}; }

.orch-progress-bar { height: 6px; background: ${T.border}; border-radius: 3px; overflow: hidden; margin-bottom: 16px; }
.orch-progress-fill { height: 100%; border-radius: 3px; transition: width 0.8s ease; background: linear-gradient(90deg, ${T.danger} 0%, ${T.warning} 50%, ${T.success} 100%); }

.orch-online-dot { width: 8px; height: 8px; border-radius: 50%; background: ${T.success}; display: inline-block; animation: onlinePulse 2s infinite; }

.orch-phase-header { display: flex; align-items: center; gap: 10px; padding: 10px 0 6px; margin-top: 12px; }
.orch-phase-header:first-child { margin-top: 0; }
.orch-phase-label { font-size: 13px; font-weight: 600; color: ${T.purple}; text-transform: uppercase; letter-spacing: 0.5px; }
.orch-phase-line { flex: 1; height: 1px; background: ${T.accentBg}; }
.orch-phase-count { font-size: 11px; color: ${T.textMuted}; }
.orch-complexity-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
.orch-complexity-badge.simple { background: ${T.successBg}; color: ${T.success}; }
.orch-complexity-badge.medium { background: ${T.warningBg}; color: ${T.warning}; }
.orch-complexity-badge.complex { background: ${T.dangerBg}; color: ${T.danger}; }

.orch-category-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
.orch-category-badge.programming { background: ${T.purpleBg}; color: ${T.purple}; }
.orch-category-badge.analysis { background: ${T.primaryBg}; color: #60a5fa; }
.orch-category-badge.content { background: ${T.successBg}; color: #4ade80; }
.orch-category-badge.generic { background: rgba(113,113,122,0.15); color: #a1a1aa; }

.orch-brain-chip { display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; border-radius: 8px; font-size: 12px; font-weight: 600; background: ${T.border}; border: 1px solid rgba(255,255,255,0.1); }
.orch-brains-section { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.orch-tool-chip { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 500; background: ${T.accentBgSubtle}; border: 1px solid ${T.accentBg}; color: #b8a4fc; }

.orch-confetti { position: fixed; top: 50%; left: 50%; pointer-events: none; z-index: 999; }
.orch-confetti-piece { position: absolute; width: 8px; height: 8px; border-radius: 2px; animation: confettiRise 2s ease forwards; }

.orch-checkmark { display: inline-block; animation: popIn 0.3s ease; color: ${T.success}; font-weight: 700; }

.orch-history-item { padding: 12px 16px; border-radius: 8px; background: var(--t-white-alpha-2); margin-bottom: 8px; cursor: pointer; transition: background 0.2s; display: flex; justify-content: space-between; align-items: center; }
.orch-history-item:hover { background: var(--t-white-alpha-5); }

.orch-desktop-only { display: none; }
@media (max-width: 899px) { .orch-page { flex-direction: column !important; } .orch-page > div { width: 100% !important; max-width: 100% !important; } .orch-detail-grid { grid-template-columns: 1fr !important; } }
@media (max-width: 600px) {
  .orch-title { font-size: 20px !important; }
  .orch-card { padding: 14px !important; }
  .orch-textarea { min-height: 100px !important; font-size: 14px !important; }
}
`;

// ── DAG View ──
function DAGView({ tasks }: { tasks: Task[] }) {
  if (!tasks.length) return null;

  // Build dependency graph and compute positions (left→right layout)
  const taskMap = new Map(tasks.map((t, i) => [t.id, { ...t, index: i }]));
  
  // Compute levels (topological sort)
  const levels: Map<string, number> = new Map();
  
  function getLevel(id: string, visited = new Set<string>()): number {
    if (levels.has(id)) return levels.get(id)!;
    if (visited.has(id)) return 0;
    visited.add(id);
    
    const task = taskMap.get(id);
    if (!task) return 0;
    
    const deps: string[] = JSON.parse(task.depends_on || '[]');
    if (deps.length === 0) {
      levels.set(id, 0);
      return 0;
    }
    
    const maxDep = Math.max(...deps.map(d => getLevel(d, visited)));
    const level = maxDep + 1;
    levels.set(id, level);
    return level;
  }
  
  tasks.forEach(t => getLevel(t.id));
  
  // Group by level
  const levelGroups: Map<number, Task[]> = new Map();
  tasks.forEach(t => {
    const lvl = levels.get(t.id) || 0;
    if (!levelGroups.has(lvl)) levelGroups.set(lvl, []);
    levelGroups.get(lvl)!.push(t);
  });

  const maxLevel = Math.max(...Array.from(levels.values()), 0);
  const nodeW = 160;
  const nodeH = 50;
  const gapX = 80;
  const gapY = 20;
  
  // Compute positions
  const positions: Map<string, { x: number; y: number }> = new Map();
  for (let lvl = 0; lvl <= maxLevel; lvl++) {
    const group = levelGroups.get(lvl) || [];
    const totalH = group.length * (nodeH + gapY) - gapY;
    const startY = Math.max(0, (300 - totalH) / 2);
    group.forEach((t, i) => {
      positions.set(t.id, {
        x: 20 + lvl * (nodeW + gapX),
        y: startY + i * (nodeH + gapY),
      });
    });
  }

  const svgW = 40 + (maxLevel + 1) * (nodeW + gapX);
  const maxNodesInLevel = Math.max(...Array.from(levelGroups.values()).map(g => g.length));
  const svgH = Math.max(200, maxNodesInLevel * (nodeH + gapY) + 40);

  const statusColors: Record<string, string> = {
    pending: T.textMuted,
    queued: T.textMuted,
    sent: T.warning,
    running: T.warning,
    completed: T.success,
    failed: T.danger,
    skipped: T.textMuted,
    cancelled: T.textMuted,
  };

  const statusAnim: Record<string, string> = {
    running: 'pulse 2s infinite',
    sent: 'pulse 2s infinite',
    completed: 'burst 0.4s ease',
    failed: 'shake 0.4s ease',
  };

  return (
    <div class="orch-card" style={{ overflow: 'auto', padding: 16 }}>
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block' }}>
        {/* Edges */}
        {tasks.map(t => {
          const deps: string[] = JSON.parse(t.depends_on || '[]');
          const to = positions.get(t.id);
          if (!to) return null;
          return deps.map(depId => {
            const from = positions.get(depId);
            if (!from) return null;
            const x1 = from.x + nodeW;
            const y1 = from.y + nodeH / 2;
            const x2 = to.x;
            const y2 = to.y + nodeH / 2;
            const depTask = taskMap.get(depId);
            const edgeColor = depTask && depTask.status === 'completed' ? T.success : T.borderHover;
            return (
              <line
                key={`${depId}-${t.id}`}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={edgeColor}
                stroke-width={2}
                stroke-dasharray="6 4"
                style={{ animation: depTask?.status === 'completed' ? 'flowDash 1s linear infinite' : 'none', transition: 'stroke 0.6s ease' }}
                marker-end="url(#arrow)"
              />
            );
          });
        })}
        
        {/* Arrow marker */}
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={T.whiteAlpha30} />
          </marker>
        </defs>
        
        {/* Nodes */}
        {tasks.map((t, i) => {
          const pos = positions.get(t.id);
          if (!pos) return null;
          const color = statusColors[t.status] || T.textMuted;
          const anim = statusAnim[t.status] || '';
          return (
            <g key={t.id} style={{ animation: anim, transformOrigin: `${pos.x + nodeW/2}px ${pos.y + nodeH/2}px`, transition: 'all 0.6s ease' }}>
              <rect
                x={pos.x} y={pos.y}
                width={nodeW} height={nodeH}
                rx={8} ry={8}
                fill={T.bg}
                stroke={color}
                stroke-width={2}
                style={{ transition: 'stroke 0.6s ease' }}
              />
              <text
                x={pos.x + nodeW/2}
                y={pos.y + nodeH/2 - 6}
                text-anchor="middle"
                fill={T.text}
                font-size={11}
                font-weight={500}
              >
                {t.title.length > 18 ? t.title.slice(0, 16) + '..' : t.title}
              </text>
              <text
                x={pos.x + nodeW/2}
                y={pos.y + nodeH/2 + 12}
                text-anchor="middle"
                fill={color}
                font-size={10}
              >
                {t.status === 'completed' ? 'done' : t.status}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Confetti ──
function Confetti() {
  const colors = [T.accent, T.success, T.warning, T.danger, T.primary, T.danger];
  return (
    <div class="orch-confetti">
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={i}
          class="orch-confetti-piece"
          style={{
            background: colors[i % colors.length],
            left: `${(Math.random() - 0.5) * 300}px`,
            top: `${(Math.random() - 0.5) * 100}px`,
            animationDelay: `${Math.random() * 0.5}s`,
            animationDuration: `${1.5 + Math.random()}s`,
          }}
        />
      ))}
    </div>
  );
}

// ── Mode Selector ──
type OrcMode = 'hub' | 'team' | 'hybrid';

const MODE_INFO: Record<OrcMode, { icon: string; name: string; desc: string; pros: string[]; cons: string[]; cost: string }> = {
  hub: {
    icon: '', name: 'Hub-Spoke',
    desc: 'Tarefas isoladas com DAG + Paralelo',
    pros: ['Rápido', 'Econômico', 'Paralelo'],
    cons: ['Sem colaboração entre agentes'],
    cost: '~$0.05/plano',
  },
  team: {
    icon: '', name: 'Multi-Agent Team',
    desc: 'Agentes formam time e debatem juntos',
    pros: ['Colaborativo', 'Criativo', 'Refina ideias'],
    cons: ['Mais tokens consumidos'],
    cost: '~$0.50/plano',
  },
  hybrid: {
    icon: '', name: 'Híbrido',
    desc: 'Grupos de trabalho + DAG entre grupos',
    pros: ['Flexível', 'Escalável', 'Melhor dos dois'],
    cons: ['Mais complexo'],
    cost: '~$0.25/plano',
  },
};

function HubDiagram() {
  return (
    <svg width="220" height="100" viewBox="0 0 220 100">
      <circle cx="110" cy="50" r="18" fill="none" stroke="#7c5cfc" stroke-width="2"/>
      <text x="110" y="54" text-anchor="middle" fill="#7c5cfc" font-size="10" font-weight="600">ORC</text>
      {[[30,25],[30,75],[190,25],[190,75]].map((coords,i) => {
        const cx = coords[0]!; const cy = coords[1]!;
        return (
          <g key={i}>
            <line x1={i<2?128:92} y1={i%2===0?42:58} x2={i<2?cx+14:cx-14} y2={cy} stroke={T.borderHover} stroke-width="1.5" stroke-dasharray="4 3"/>
            <circle cx={cx} cy={cy} r="12" fill="none" stroke={i<2?T.warning:T.success} stroke-width="1.5"/>
            <text x={cx} y={cy+4} text-anchor="middle" fill={T.textSecondary} font-size="8">A{i+1}</text>
          </g>
        );
      })}
      <text x="110" y="92" text-anchor="middle" fill={T.textMuted} font-size="9">Resultado</text>
    </svg>
  );
}

function TeamDiagram() {
  const agents = [[60,30],[160,30],[110,80],[60,80],[160,80]];
  return (
    <svg width="220" height="100" viewBox="0 0 220 100">
      {agents.slice(0,3).map(([x,y],i) =>
        agents.slice(i+1,3).map(([x2,y2],j) => (
          <line key={`${i}-${j}`} x1={x} y1={y} x2={x2} y2={y2} stroke={T.accentBg} stroke-width="1"/>
        ))
      )}
      {agents.slice(0,3).map((coords,i) => {
        const ax = coords[0]!; const ay = coords[1]!;
        return (
          <g key={i}>
            <circle cx={ax} cy={ay} r="14" fill="none" stroke="#7c5cfc" stroke-width="1.5"/>
            <text x={ax} y={ay+4} text-anchor="middle" fill={T.text} font-size="9"></text>
          </g>
        );
      })}
      <text x="110" y="14" text-anchor="middle" fill={T.textMuted} font-size="9">Conversa compartilhada</text>
    </svg>
  );
}

function HybridDiagram() {
  return (
    <svg width="220" height="100" viewBox="0 0 220 100">
      <rect x="10" y="15" width="80" height="70" rx="8" fill="none" stroke={T.warning} stroke-width="1.5" stroke-dasharray="4 3"/>
      <text x="50" y="12" text-anchor="middle" fill={T.warning} font-size="9">Team A</text>
      <circle cx="35" cy="45" r="10" fill="none" stroke="#7c5cfc" stroke-width="1.2"/>
      <circle cx="65" cy="45" r="10" fill="none" stroke="#7c5cfc" stroke-width="1.2"/>
      <line x1="45" y1="45" x2="55" y2="45" stroke={T.accentBgStrong} stroke-width="1"/>
      <text x="50" y="72" text-anchor="middle" fill={T.textMuted} font-size="8"></text>
      <line x1="95" y1="50" x2="125" y2="50" stroke={T.success} stroke-width="2" marker-end="url(#arrowH)"/>
      <rect x="130" y="15" width="80" height="70" rx="8" fill="none" stroke={T.success} stroke-width="1.5" stroke-dasharray="4 3"/>
      <text x="170" y="12" text-anchor="middle" fill={T.success} font-size="9">Team B</text>
      <circle cx="155" cy="45" r="10" fill="none" stroke="#7c5cfc" stroke-width="1.2"/>
      <circle cx="185" cy="45" r="10" fill="none" stroke="#7c5cfc" stroke-width="1.2"/>
      <line x1="165" y1="45" x2="175" y2="45" stroke={T.accentBgStrong} stroke-width="1"/>
      <text x="170" y="72" text-anchor="middle" fill={T.textMuted} font-size="8"></text>
      <defs><marker id="arrowH" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M 0 0 L 10 5 L 0 10 z" fill={T.success}/></marker></defs>
    </svg>
  );
}

const MODE_EXPLAINS: Record<OrcMode, { title: string; text: string; ideal: string; Diagram: () => any }> = {
  hub: {
    title: 'Como funciona o Hub-Spoke',
    text: 'Seu pedido é dividido em tarefas independentes. Cada agente recebe UMA tarefa específica, executa sozinho e devolve o resultado. O orquestrador combina tudo no final.',
    ideal: 'Tarefas paralelas e bem definidas. Ex: "Pesquise 5 concorrentes" — cada agente pesquisa 1.',
    Diagram: HubDiagram,
  },
  team: {
    title: 'Como funciona o Multi-Agent Team',
    text: 'Todos os agentes entram numa conversa compartilhada. Eles podem discutir, debater, corrigir uns aos outros e chegar numa solução colaborativa. Funciona como um brainstorm em equipe.',
    ideal: 'Problemas complexos que exigem discussão. Ex: "Projetem a arquitetura de um novo sistema".',
    Diagram: TeamDiagram,
  },
  hybrid: {
    title: 'Como funciona o Híbrido',
    text: 'Seu pedido é dividido em grupos de trabalho. Dentro de cada grupo, os agentes colaboram em equipe. Entre grupos, funciona como pipeline — o resultado de um grupo alimenta o próximo.',
    ideal: 'Projetos grandes com múltiplas fases. Ex: "Criar sistema com backend, frontend e testes".',
    Diagram: HybridDiagram,
  },
};

function ModeSelector({ mode, setMode }: { mode: OrcMode; setMode: (m: OrcMode) => void }) {
  const [expanded, setExpanded] = useState<OrcMode | null>(null);
  const explain = expanded ? MODE_EXPLAINS[expanded] : null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div class="orch-mode-grid">
        {(['hub', 'team', 'hybrid'] as OrcMode[]).map(m => {
          const info = MODE_INFO[m];
          const isSelected = mode === m;
          return (
            <div
              key={m}
              class={`orch-mode-card ${isSelected ? 'selected' : ''}`}
              onClick={() => setMode(m)}
              style={{ padding: '14px 16px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 22 }}>{info.icon}</span>
                <div style={{ flex: 1 }}>
                  <div class="orch-mode-name" style={{ fontSize: 14, marginBottom: 2 }}>{info.name}</div>
                  <div style={{ fontSize: 11, color: T.textSecondary }}>{info.desc}</div>
                </div>
                <span style={{ fontSize: 11, color: T.accent, whiteSpace: 'nowrap' }}>{info.cost.replace('', '')}</span>
              </div>
              <div
                style={{ fontSize: 11, color: T.textMuted, marginTop: 6, textAlign: 'center', cursor: 'pointer' }}
                onClick={(e: Event) => { e.stopPropagation(); setExpanded(expanded === m ? null : m); }}
              >
                {expanded === m ? '▲ Fechar detalhes' : '▼ Ver detalhes'}
              </div>
            </div>
          );
        })}
      </div>
      {expanded && explain && (
        <div class="orch-mode-explain" key={expanded}>
          <div class="orch-mode-explain-inner">
            <h4>{explain.title}</h4>
            <p>{explain.text}</p>
            <explain.Diagram />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 500, color: T.success, marginBottom: 6 }}>Vantagens</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {MODE_INFO[expanded].pros.map(p => <li key={p} style={{ fontSize: 12, color: T.textSecondary, padding: '2px 0' }}>{p}</li>)}
                </ul>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 500, color: T.warning, marginBottom: 6 }}>Limitações</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {MODE_INFO[expanded].cons.map(c => <li key={c} style={{ fontSize: 12, color: T.textSecondary, padding: '2px 0' }}>{c}</li>)}
                </ul>
              </div>
            </div>
            <div class="ideal">
              <strong>Ideal para:</strong> {explain.ideal}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Team Chat View ──
interface ChannelMsg {
  id: string;
  channel_id: string;
  runner_id: string | null;
  agent_name: string | null;
  role: string;
  content: string;
  round_number: number;
  created_at: string;
}

interface Channel {
  id: string;
  plan_id: string;
  team_name: string;
  team_objective: string;
  status: string;
  max_rounds: number;
  current_round: number;
  created_at: string;
  completed_at: string | null;
}

function TeamChatView({ channel, messages, onNudge }: { channel: Channel; messages: ChannelMsg[]; onNudge?: () => void }) {
  const chatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages.length]);

  const elapsed = Math.round((Date.now() - new Date(channel.created_at).getTime()) / 60000);
  const isActive = channel.status === 'active';
  const converging = channel.current_round > 10;

  return (
    <div class="orch-card" style={{ animation: 'fadeInUp 0.3s ease' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>
            {channel.team_name}
          </div>
          <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>
            Round {channel.current_round}/{channel.max_rounds} · {messages.length} msgs · {elapsed}min
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Convergence indicator */}
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            background: converging ? T.warning : T.success,
            boxShadow: `0 0 6px ${converging ? T.warning : T.success}`,
          }} title={converging ? 'Pode estar divergindo' : 'Progredindo'} />
          <span class={`orch-status-pill ${channel.status === 'completed' ? 'completed' : 'running'}`}>
            {channel.status}
          </span>
          {isActive && onNudge && (
            <Button variant="ghost" size="xs" style={{ height: 30, padding: '0 12px', fontSize: 12 }} onClick={onNudge}>
              Nudge
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={chatRef} style={{
        maxHeight: 400, overflowY: 'auto', scrollBehavior: 'smooth',
        background: T.bg, borderRadius: 8, padding: 12,
      }}>
        {messages.map((msg, i) => {
          const isSystem = msg.role === 'system';
          return (
            <div key={msg.id} style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 8,
              background: isSystem ? T.accentBg : T.bgCard,
              borderLeft: `3px solid ${isSystem ? T.accent : T.textMuted}`,
              animation: i === messages.length - 1 ? 'fadeInUp 0.3s ease' : 'none',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: isSystem ? T.accent : T.purple }}>
                  {isSystem ? 'SISTEMA' : `${msg.agent_name || 'Agente'}`}
                </span>
                <span style={{ fontSize: 11, color: T.textMuted }}>R{msg.round_number}</span>
              </div>
              <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {msg.content.replace(/\[TEAM_MSG\]|\[\/TEAM_MSG\]|\[TEAM_DONE\]|\[\/TEAM_DONE\]/g, '').trim()}
              </div>
            </div>
          );
        })}
        {isActive && (
          <div style={{ padding: '8px 14px', color: T.textMuted, fontSize: 12, animation: 'pulse 2s infinite' }}>
            Aguardando próximo turno...
          </div>
        )}
      </div>
    </div>
  );
}

// ── Team DAG View (Hybrid mode) ──
function TeamDAGView({ channels, messages, onExpand, expandedId, onNudge }: {
  channels: Channel[];
  messages: ChannelMsg[];
  onExpand: (id: string | null) => void;
  expandedId: string | null;
  onNudge: (id: string) => void;
}) {
  if (!channels.length) return null;

  const statusColors: Record<string, string> = {
    active: T.warning,
    completed: T.success,
    pending: T.textMuted,
    failed: T.danger,
  };

  return (
    <div>
      <div class="orch-card" style={{ padding: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 500, color: T.text, marginBottom: 12 }}>Equipes</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {channels.map(ch => {
            const color = statusColors[ch.status] || T.textMuted;
            const chMsgs = messages.filter(m => m.channel_id === ch.id);
            const isExpanded = expandedId === ch.id;
            return (
              <div
                key={ch.id}
                onClick={() => onExpand(isExpanded ? null : ch.id)}
                style={{
                  minWidth: 180, padding: 16, borderRadius: 12, cursor: 'pointer',
                  background: isExpanded ? T.accentBg : T.bg,
                  border: `2px solid ${isExpanded ? T.accent : color}`,
                  transition: 'all 0.3s',
                  animation: ch.status === 'active' ? 'pulse 2s infinite' : 'none',
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>
                  {ch.team_name}
                </div>
                <div style={{ fontSize: 12, color: T.textSecondary }}>
                  {chMsgs.length} msgs · R{ch.current_round}/{ch.max_rounds}
                </div>
                <div style={{ marginTop: 8 }}>
                  <span class={`orch-status-pill ${ch.status === 'active' ? 'running' : ch.status}`}>{ch.status}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {expandedId && (() => {
        const ch = channels.find(c => c.id === expandedId);
        if (!ch) return null;
        const chMsgs = messages.filter(m => m.channel_id === expandedId);
        return (
          <div style={{ animation: 'expandDown 0.3s ease' }}>
            <TeamChatView channel={ch} messages={chMsgs} onNudge={() => onNudge(expandedId)} />
          </div>
        );
      })()}
    </div>
  );
}

// ── Main Component ──
export function OrchestratorPage({ path }: { path?: string }) {
  const [prompt, setPrompt] = useState('');
  const [preferredTools, setPreferredTools] = useState('');
  const [mode, setMode] = useState<OrcMode>('hub');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showOffline, setShowOffline] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [selectedExecModels, setSelectedExecModels] = useState<string[]>([]);
  const [activePlan, setActivePlan] = useState<Plan | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [events, setEvents] = useState<OrcEvent[]>([]);
  const [history, setHistory] = useState<Plan[]>([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [agents, setAgents] = useState<any[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelMessages, setChannelMessages] = useState<ChannelMsg[]>([]);
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<{id: string; name: string}[]>([]);
  const [selectedModel, setSelectedModel] = useState('auto');
  const [requiredTools, setRequiredTools] = useState<any[]>([]);
  const [roleAssignments, setRoleAssignments] = useState<Record<string, string>>({});
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [toolSelections, setToolSelections] = useState<Record<string, string>>({});
  const [loadingTools, setLoadingTools] = useState(false);
  const [planMeta, setPlanMeta] = useState<{ complexity?: string; phases?: string[] }>({});
  const [estimate, setEstimate] = useState<any>(null);
  const [estimating, setEstimating] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<number | null>(null);
  // Inline credential form states
  const [credentialFormOpen, setCredentialFormOpen] = useState<string | null>(null);
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});
  const [credentialTarget, setCredentialTarget] = useState<'account' | string>('account');
  const [credentialSaving, setCredentialSaving] = useState(false);
  const [credentialMsg, setCredentialMsg] = useState('');
  const [credentialPending, setCredentialPending] = useState<Set<string>>(new Set());

  // Extract phases/complexity from plan_json
  const extractPlanMeta = (plan: any) => {
    try {
      const pj = typeof plan?.plan_json === 'string' ? JSON.parse(plan.plan_json) : (plan?.plan_json || {});
      setPlanMeta({ complexity: pj.complexity, phases: pj.phases });
    } catch { setPlanMeta({}); }
  };

  // Gate checks
  // if (!isAdmin.value && !hasActivePlan.value) return <NoPlanGate />;

  // Load history + agent count
  useEffect(() => {
    loadHistory();
    loadAgents();
    loadModels();
  }, []);

  // Polling during execution — also advances SDD steps
  useEffect(() => {
    if (activePlan && (activePlan.status === 'executing')) {
      const pollAndAdvance = async () => {
        // Advance one SDD step (fire-and-forget, backend handles idempotency)
        try {
          await api.post(`/orchestrator/plans/${activePlan.id}/execute-sdd`, undefined, { timeout: 120000 });
        } catch {}
        // Then refresh status
        await pollStatus(activePlan.id);
      };
      pollRef.current = window.setInterval(pollAndAdvance, 6000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
  }, [activePlan?.id, activePlan?.status]);

  // Poll events + auto-drive generation when viewing a generating plan
  useEffect(() => {
    if (!activePlan || activePlan.status !== 'generating') return;
    let cancelled = false;
    let driving = false;

    const refreshEvents = async () => {
      try {
        const r: any = await api.get(`/orchestrator/plans/${activePlan.id}`);
        if (cancelled) return;
        setActivePlan(r.plan); setEvents(r.events || []); setTasks(r.tasks || []);
        if (r.plan?.status !== 'generating') loadHistory();
      } catch {}
    };

    let failures = 0;
    const MAX_FAILURES = 5;

    const drive = async () => {
      if (driving || cancelled || failures >= MAX_FAILURES) return;
      driving = true;
      try {
        const r: any = await api.get(`/orchestrator/plans/${activePlan.id}`);
        if (cancelled) return;
        setActivePlan(r.plan); setEvents(r.events || []); setTasks(r.tasks || []);
        if (r.plan?.status !== 'generating') { loadHistory(); return; }

        const pj = typeof r.plan.plan_json === 'string' ? JSON.parse(r.plan.plan_json) : (r.plan.plan_json || {});
        if (!pj.skeleton) {
          await api.post(`/orchestrator/plans/${activePlan.id}/generate-skeleton`, {}, { timeout: 120000 });
        } else if (pj.nextPhase < pj.totalPhases) {
          await api.post(`/orchestrator/plans/${activePlan.id}/generate-phase`, {}, { timeout: 120000 });
        } else {
          await api.post(`/orchestrator/plans/${activePlan.id}/finalize`, {}, { timeout: 60000 });
        }
        failures = 0; // Reset on success
        if (!cancelled) await refreshEvents();
      } catch (e: any) {
        failures++;
        if (e?.code === 'INSUFFICIENT_BALANCE') { setError('Saldo insuficiente.'); failures = MAX_FAILURES; }
        else if (failures >= MAX_FAILURES) { setError(`Erro na geração após ${MAX_FAILURES} tentativas. Tente novamente mais tarde.`); }
      } finally {
        driving = false;
      }
    };

    // Start driving immediately, then poll events every 5s (drive only if not busy)
    drive();
    const eventPoll = window.setInterval(refreshEvents, 5000);
    const drivePoll = window.setInterval(drive, 15000);
    return () => { cancelled = true; clearInterval(eventPoll); clearInterval(drivePoll); };
  }, [activePlan?.id, activePlan?.status === 'generating']);

  // Poll for generating plans in history
  useEffect(() => {
    const generatingPlans = history.filter(p => p.status === 'generating');
    if (generatingPlans.length === 0) return;
    const interval = window.setInterval(async () => {
      let changed = false;
      for (const gp of generatingPlans) {
        try {
          const full: any = await api.get(`/orchestrator/plans/${gp.id}`);
          const newStatus = full.plan?.status;
          if (newStatus && newStatus !== 'generating') {
            changed = true;
          }
        } catch {}
      }
      if (changed) loadHistory();
    }, 5000);
    return () => clearInterval(interval);
  }, [history.map(h => h.id + h.status).join(',')]);

  // Auto-scroll timeline
  useEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
    }
  }, [events.length]);

  const loadHistory = async () => {
    try {
      const r: any = await api.get('/orchestrator/plans?limit=10');
      setHistory(r.plans || []);
    } catch {}
  };

  const loadModels = async () => {
    try {
      const r: any = await api.get('/orchestrator/available-models');
      const models = r.models || [];
      setAvailableModels(models);
      // Pre-select all models for execution
      setSelectedExecModels(models.map((m: any) => m.id));
    } catch {}
  };

  const loadAgents = async () => {
    try {
      const r: any = await api.get('/runner-v3/list');
      const runnerList = r?.data?.runners || r?.runners || [];
      const allAgents = runnerList.map((a: any) => ({
        id: a.id,
        agent_name: a.agent_name || a.name || a.machine_name || 'Agent',
        name: a.agent_name || a.name || a.machine_name || 'Agent',
        model: a.model || a.agent_model || '',
        status: a.status,
        machine_id: a.machine_id || null,
      }));
      setAgents(allAgents);
    } catch {}
  };

  const toggleAgent = (agentId: string) => {
    setSelectedAgents(prev =>
      prev.includes(agentId) ? prev.filter(id => id !== agentId) : [...prev, agentId]
    );
  };

  const onlineAgents = agents.filter(a => a.status === 'online');
  const agentCount = onlineAgents.length;

  const getEstimate = async () => {
    if (!prompt.trim() || prompt.trim().length < 10) return;
    setEstimating(true);
    setError('');
    setEstimate(null);
    try {
      const r: any = await api.post('/orchestrator/estimate', { prompt: prompt.trim(), model: selectedModel, mode, selectedRunnerIds: selectedAgents, preferred_tools: preferredTools.trim() || undefined }, { timeout: 60000 });
      const est = r.estimate || r;
      setEstimate(est);
      // Navigate to plan detail page
      if (est.id) {
        loadHistory();
        loadPlan(est.id);
      }
    } catch (e: any) {
      if (e.code === 'INSUFFICIENT_BALANCE') {
        setError('Saldo insuficiente. Recarregue sua carteira para continuar usando o Orquestrador.');
      } else {
        setError(e.message || 'Falha ao analisar projeto');
      }
    } finally {
      setEstimating(false);
    }
  };

  const createPlan = async () => {
    if (!prompt.trim() || prompt.trim().length < 10) return;
    setCreating(true);
    setError('');
    // Add to history optimistically as "generating" BEFORE the request
    const tempId = `temp_${Date.now()}`;
    const newPlan: Plan = {
      id: tempId,
      title: estimate?.title || 'Gerando plano...',
      status: 'generating',
      total_tasks: estimate?.estimated_tasks || 0,
      completed_tasks: 0,
      failed_tasks: 0,
      created_at: new Date().toISOString(),
      original_prompt: prompt.trim(),
      estimated_duration_min: estimate?.estimated_total_min || 0,
      plan_json: JSON.stringify(estimate || {}),
      started_at: null, completed_at: null, actual_duration_min: null, final_output: null,
    };
    setHistory(prev => [newPlan, ...prev]);
    const savedPrompt = prompt.trim();
    const savedEstimateId = estimate?.id;
    setPrompt('');
    setEstimate(null);

    try {
      // Queue plan (returns immediately) then redirect — auto-drive handles generation
      const r: any = await api.post('/orchestrator/plans', {
        prompt: savedPrompt, selectedRunnerIds: selectedAgents, mode, model: selectedModel,
        estimateId: savedEstimateId, executionModels: selectedExecModels, preferred_tools: preferredTools.trim() || undefined,
      }, { timeout: 30000 });

      const realId = r.planId;
      if (realId) {
        setHistory(prev => prev.map(p => p.id === tempId ? { ...p, id: realId, status: 'generating' } : p));
        // Immediately navigate to the generating plan — auto-drive useEffect takes over
        loadPlan(realId);
      }
      loadHistory();
    } catch (e: any) {
      if (e.code === 'INSUFFICIENT_BALANCE') {
        setError('Saldo insuficiente. Recarregue sua carteira para criar planos.');
      } else {
        setError(e.message || 'Failed to create plan');
      }
      loadHistory();
    } finally {
      setCreating(false);
    }
  };

  const executePlan = async () => {
    if (!activePlan) return;
    setLoading(true);
    try {
      // Kick off SDD execution — first call starts the plan and runs 1 step
      const r: any = await api.post(`/orchestrator/plans/${activePlan.id}/execute-sdd`, undefined, { timeout: 180000 });
      // Refresh plan state from status endpoint (execute-sdd returns { planId, executed, allDone })
      await pollStatus(activePlan.id);
      // Polling useEffect will continue advancing via pollAndAdvance
    } catch (e: any) {
      if (e.code === 'INSUFFICIENT_BALANCE') {
        setError('Saldo insuficiente. Recarregue sua carteira para executar tarefas.');
      } else {
        setError(e.message || 'Failed to execute plan');
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelPlan = async () => {
    if (!activePlan) return;
    try {
      await api.post(`/orchestrator/plans/${activePlan.id}/cancel`);
      setActivePlan(null);
      setTasks([]);
      setEvents([]);
      loadHistory();
    } catch {}
  };

  // Create a real plan from an estimated plan
  const createPlanFromEstimate = async () => {
    if (!activePlan || (activePlan.status !== 'estimated' && activePlan.status !== 'researched')) return;
    setCreating(true);
    setError('');
    try {
      const pj = typeof activePlan.plan_json === 'string' ? JSON.parse(activePlan.plan_json) : (activePlan.plan_json || {});
      const savedPrompt = activePlan.original_prompt;
      const savedModel = pj.model || selectedModel;
      const savedMode = pj.mode || activePlan.mode || mode;
      const savedRunnerIds = pj.selectedRunnerIds || selectedAgents;

      // Queue plan (returns immediately) then redirect — auto-drive handles generation
      const r: any = await api.post('/orchestrator/plans', {
        prompt: savedPrompt, selectedRunnerIds: savedRunnerIds, mode: savedMode, model: savedModel,
        estimateId: activePlan.id,
        executionModels: selectedExecModels,
      }, { timeout: 30000 });

      const realId = r.planId;
      // Immediately navigate to the generating plan — auto-drive useEffect takes over
      if (realId) loadPlan(realId);
      loadHistory();
    } catch (e: any) {
      if (e.code === 'INSUFFICIENT_BALANCE') {
        setError('Saldo insuficiente. Recarregue sua carteira para criar planos.');
      } else {
        setError(e.message || 'Erro ao criar plano');
      }
    } finally {
      setCreating(false);
    }
  };

  // Delete an estimated plan
  const deleteEstimate = async () => {
    if (!activePlan) return;
    try {
      await api.delete(`/orchestrator/plans/${activePlan.id}`);
      setActivePlan(null);
      loadHistory();
    } catch {}
  };

  const pollStatus = async (planId: string) => {
    try {
      const r: any = await api.get(`/orchestrator/plans/${planId}/status`);
      setActivePlan(r.plan); extractPlanMeta(r.plan);
      setTasks(r.tasks || []);
      setEvents(r.events || []);
      if (r.channels) setChannels(r.channels);
      if (r.channelMessages) setChannelMessages(r.channelMessages);

      if (r.plan.status === 'completed') {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        if (pollRef.current) clearInterval(pollRef.current);
        loadHistory();
      } else if (r.plan.status === 'failed' || r.plan.status === 'cancelled') {
        if (pollRef.current) clearInterval(pollRef.current);
        loadHistory();
      }
    } catch {}
  };

  const nudgeChannel = async (channelId: string) => {
    try { await api.post(`/orchestrator/channels/${channelId}/nudge`); } catch {}
  };

  const loadRequiredTools = async (planId: string) => {
    setLoadingTools(true);
    try {
      const r: any = await api.get(`/orchestrator/plans/${planId}/required-tools`);
      setRequiredTools(r.tools || []);
      // Auto-select first credential for each tool
      const selections: Record<string, string> = {};
      for (const tool of (r.tools || [])) {
        if (tool.credentials?.length > 0) {
          selections[tool.provider] = tool.credentials[0].runner_id;
        }
      }
      setToolSelections(selections);
    } catch {} finally { setLoadingTools(false); }
  };

  const saveCredential = async (providerId: string) => {
    setCredentialSaving(true);
    setCredentialMsg('');
    try {
      const credentials: Record<string, string> = {};
      for (const [key, val] of Object.entries(credentialValues)) {
        if (val.trim()) credentials[key] = val.trim();
      }
      if (Object.keys(credentials).length === 0) {
        setCredentialMsg('Preencha pelo menos um campo');
        setCredentialSaving(false);
        return;
      }
      
      const isAgent = credentialTarget !== 'account';
      const agent = isAgent ? agents.find(a => a.id === credentialTarget) : null;
      
      await api.post('/orchestrator/credentials/save', {
        provider: providerId,
        credentials,
        target_type: isAgent ? 'machine' : 'account',
        machine_id: agent?.machine_id || undefined,
      });
      
      setCredentialMsg('Credencial salva com sucesso!');
      setCredentialValues({});
      setTimeout(() => {
        setCredentialFormOpen(null);
        setCredentialMsg('');
        // Refresh required tools to show updated status
        if (activePlan) loadRequiredTools(activePlan.id);
      }, 1500);
    } catch (err: any) {
      console.error('[Orchestrator] saveCredential error:', err);
      const msg = err?.data?.error || err?.message || err?.error || 'Erro ao salvar credencial';
      setCredentialMsg(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setCredentialSaving(false);
    }
  };

  // pollCredentialStatus removed — credentials now save directly (no pending queue)

  const loadPlan = async (planId: string) => {
    setLoading(true);
    try {
      const r: any = await api.get(`/orchestrator/plans/${planId}`);
      setActivePlan(r.plan); extractPlanMeta(r.plan);
      setTasks(r.tasks || []);
      setEvents(r.events || []);
      if (r.plan?.status === 'ready') loadRequiredTools(planId);
    } catch {} finally { setLoading(false); }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatEventType = (type: string) => {
    const map: Record<string, string> = {
      plan_created: 'Plano criado',
      plan_started: 'Execucao iniciada',
      plan_completed: 'Plano concluido',
      plan_failed: 'Plano falhou',
      plan_cancelled: 'Plano cancelado',
      task_assigned: 'Tarefa atribuida',
      task_sent: 'Tarefa enviada',
      task_completed: 'Tarefa concluida',
      task_failed: 'Tarefa falhou',
      task_retried: 'Tarefa reenviada',
      task_timeout: 'Tarefa timeout',
    };
    return map[type] || type;
  };

  const progressPct = activePlan ? Math.round(((activePlan.completed_tasks || 0) / Math.max(activePlan.total_tasks, 1)) * 100) : 0;

  // Elapsed time
  const elapsed = activePlan?.started_at
    ? Math.round((Date.now() - new Date(activePlan.started_at).getTime()) / 60000)
    : 0;

  // Task dependency labels
  const taskIdToNum = new Map(tasks.map((t, i) => [t.id, i + 1]));

  return (
    <>
      <style>{STYLES}</style>
      <div class="orch-desktop-only">
        <h2 style={{ color: T.text, marginBottom: 8 }}>Desktop Only</h2>
        <p>O Orquestrador requer uma tela de pelo menos 900px de largura.</p>
      </div>
      <div class="orch-page">
        <h1 class="orch-title">Orquestrador de Tarefas</h1>

        {showConfetti && <Confetti />}

        {error && (
          <div class="orch-card" style={{ borderLeft: `4px solid ${T.danger}`, animation: 'fadeInUp 0.3s ease' }}>
            <div style={{ color: T.danger, fontSize: 14 }}>{error}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {error.toLowerCase().includes('saldo') && (
                <a href="/profile" onClick={(e: any) => { e.preventDefault(); window.location.href = '/profile?tab=wallet'; }} class="orch-btn orch-btn-primary" style={{ fontSize: 13, padding: '8px 16px', textDecoration: 'none' }}>
                  Recarregar Carteira
                </a>
              )}
              <Button variant="ghost" onClick={() => setError('')}>Fechar</Button>
            </div>
          </div>
        )}

        {loading && (<div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div>)}

        {/* ── STATE 1: Input ── */}
        {!activePlan && !loading && (
          <div style={{ animation: 'crossfadeIn 0.5s ease' }}>
            <ModeSelector mode={mode} setMode={setMode} />
            <div>
              {/* Textarea */}
              <div class="orch-card">
                <Textarea
                  class="orch-textarea"
                  placeholder="Descreva o que voce precisa. Seja o mais detalhado possivel..."
                  value={prompt}
                  onInput={(e: any) => { setPrompt(e.target.value); if (estimate) setEstimate(null); }}
                  disabled={creating}
                />
                <input
                  type="text"
                  class="orch-textarea"
                  placeholder="Ferramentas que voce ja usa ou prefere (ex: React, Cloudflare, Tailwind, Figma...)"
                  value={preferredTools}
                  onInput={(e: any) => { setPreferredTools(e.target.value); if (estimate) setEstimate(null); }}
                  disabled={creating}
                  style={{ minHeight: 'auto', height: 42, resize: 'none', marginTop: 8, fontSize: 13 }}
                />
                {/* Estimate Card */}
                {estimate && (
                  <div style={{
                    background: T.accentBg, border: `1px solid ${T.accentBg}`,
                    borderRadius: 10, padding: 16, marginBottom: 12, animation: 'fadeInUp 0.3s ease',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Análise do Projeto</span>
                        {estimate.category && (
                          <span class={`orch-category-badge ${estimate.category}`}>
                            {estimate.category === 'programming' ? '' : estimate.category === 'analysis' ? '' : estimate.category === 'content' ? '' : ''}
                            {' '}{estimate.category === 'programming' ? 'Programação' : estimate.category === 'analysis' ? 'Análise' : estimate.category === 'content' ? 'Conteúdo' : 'Geral'}
                          </span>
                        )}
                      </div>
                      <span class={`orch-complexity-badge ${estimate.complexity || 'medium'}`}>
                        {estimate.complexity === 'simple' ? 'Simples' : estimate.complexity === 'complex' ? 'Complexo' : 'Médio'}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                      <div style={{ textAlign: 'center', padding: '8px 0', background: T.bgOverlay, borderRadius: 8 }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: T.accent }}>{estimate.estimated_tasks || '?'}</div>
                        <div style={{ fontSize: 11, color: T.textSecondary }}>Tasks</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 0', background: T.bgOverlay, borderRadius: 8 }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: T.warning }}>{estimate.estimated_phases || '?'}</div>
                        <div style={{ fontSize: 11, color: T.textSecondary }}>Fases</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 0', background: T.bgOverlay, borderRadius: 8 }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: T.success }}>${(estimate.estimated_cost_usd || 0).toFixed(2)}</div>
                        <div style={{ fontSize: 11, color: T.textSecondary }}>Custo est.</div>
                      </div>
                    </div>
                    {estimate.estimated_total_min && (
                      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>
 Tempo estimado: ~{estimate.estimated_total_min < 60 ? `${estimate.estimated_total_min}min` : `${Math.round(estimate.estimated_total_min / 60)}h${estimate.estimated_total_min % 60 > 0 ? ` ${estimate.estimated_total_min % 60}min` : ''}`}
                      </div>
                    )}
                    {estimate.phases_preview && estimate.phases_preview.length > 0 && (
                      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8 }}>
                        {estimate.phases_preview.map((p: any, i: number) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                            <span>{p.name}</span>
                            <span style={{ color: T.textMuted }}>{p.tasks_count} tasks • ~{p.estimated_min}min</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {estimate.summary && <div style={{ fontSize: 12, color: T.textSecondary, fontStyle: 'italic', marginBottom: 8 }}>{estimate.summary}</div>}
                    <div style={{ fontSize: 11, color: T.textMuted, borderTop: `1px solid ${T.border}`, paddingTop: 8, display: 'flex', gap: 12 }}>
                      <span>Pesquisa</span><span style={{ color: T.border }}>→</span>
                      <span>Especificação</span><span style={{ color: T.border }}>→</span>
                      <span>Execução</span><span style={{ color: T.border }}>→</span>
                      <span>Revisão</span>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                  <Select
                    value={selectedModel}
                    onChange={setSelectedModel}
                    options={availableModels.length > 0 ? availableModels.map(m => ({ value: m.id, label: m.name })) : [{ value: 'auto', label: 'Auto (Smart Routing)' }]}
                    style={{ width: 180, height: 40, fontSize: 13, padding: '0 10px', flexShrink: 0 }}
                  />
                  {!estimate ? (
                    <Button
                      variant="primary"
                      onClick={getEstimate}
                      disabled={estimating || prompt.trim().length < 10}
                      style={{ flex: 1, height: 40 }}
                    >
                      {estimating ? 'Analisando projeto...' : 'Analisar e Planejar'}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="primary"
                        onClick={createPlan}
                        disabled={creating || selectedAgents.length === 0}
                        style={{ flex: 1, height: 40 }}
                      >
                        {creating ? 'Montando plano...' : `Criar Plano (${selectedAgents.length} agente${selectedAgents.length !== 1 ? 's' : ''})`}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setEstimate(null)}
                        style={{ padding: '8px 12px', height: 40 }}
                      >
 
                      </Button>
                    </>
                  )}
                </div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8, lineHeight: 1.5, textAlign: 'center' }}>
                  A análise e execução utilizam o Elon Router (LLM). O custo é debitado automaticamente do saldo da sua carteira.
                </div>
              </div>
            </div>

            {/* Agents selector — shown only after estimate, online only */}
            {estimate && (() => {
              const onl = agents.filter(a => a.status === 'online');
              const offl = agents.filter(a => a.status !== 'online');
              return (
                <div class="orch-card" style={{ marginTop: 12, animation: 'fadeInUp 0.3s ease' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: T.text }}>Selecione os Agentes ({onl.length} online)</div>
                    <span style={{ fontSize: 11, color: T.textSecondary }}>{selectedAgents.length} selecionado{selectedAgents.length !== 1 ? 's' : ''}</span>
                  </div>
                  {onl.length === 0 ? (
                    <div style={{ color: T.textMuted, fontSize: 13, fontStyle: 'italic' }}>Nenhum agente online.</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                      {onl.map(agent => {
                        const isChecked = selectedAgents.includes(agent.id);
                        return (
                          <div key={agent.id} onClick={() => toggleAgent(agent.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                              borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s',
                              background: isChecked ? T.accentBg : T.bgCard,
                              border: `1px solid ${isChecked ? T.accentBg : T.border}`,
                            }}>
                            <div style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, border: `2px solid ${isChecked ? T.accent : T.borderHover}`, background: isChecked ? T.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {isChecked && <span style={{ color: T.text, fontSize: 10, lineHeight: 1 }}></span>}
                            </div>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.success, flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: isChecked ? T.text : T.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.agent_name || 'Agent'}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {offl.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div onClick={() => setShowOffline(!showOffline)} style={{ fontSize: 11, color: T.textMuted, cursor: 'pointer', userSelect: 'none' }}>
                        {showOffline ? '▲' : '▼'} {offl.length} agente{offl.length !== 1 ? 's' : ''} offline
                      </div>
                      {showOffline && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginTop: 8 }}>
                          {offl.map(agent => (
                            <div key={agent.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, opacity: 0.4, background: T.bgCard, border: `1px solid ${T.bgCard}` }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.textMuted, flexShrink: 0 }} />
                              <span style={{ fontSize: 12, color: T.textMuted }}>{agent.agent_name || 'Agent'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* History */}
            {history.length > 0 && (
              <div class="orch-card" style={{ marginTop: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 500, color: T.text, marginBottom: 12 }}>Planos anteriores</div>
                {(showAllHistory ? history : history.slice(0, 3)).map(p => (
                  <div key={p.id} class="orch-history-item" onClick={() => loadPlan(p.id)} style={p.status === 'generating' ? { borderLeft: `3px solid ${T.warning}` } : {}}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: T.text, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {p.status === 'generating' && <span class="orch-pulse-dot" />}
                        {p.title}
                      </div>
                      <div style={{ color: T.textMuted, fontSize: 12, marginTop: 2 }}>
                        {new Date(p.created_at).toLocaleDateString('pt-BR')} — {p.status === 'generating' ? 'Criando plano...' : `${p.total_tasks} tarefas`}
                      </div>
                    </div>
                    <span class={`orch-status-pill ${p.status}`}>
                      {{ estimated: 'pesquisado', researched: 'pesquisado', generating: 'gerando', ready: 'pronto', executing: 'executando', completed: 'concluído', failed: 'erro', cancelled: 'cancelado' }[p.status] || p.status}
                    </span>
                  </div>
                ))}
                {history.length > 3 && !showAllHistory && (
                  <div
                    onClick={() => setShowAllHistory(true)}
                    style={{ fontSize: 12, color: T.accent, cursor: 'pointer', textAlign: 'center', padding: '10px 0', marginTop: 4 }}
                  >
                    Ver todos ({history.length - 3} restantes)
                  </div>
                )}
                {showAllHistory && history.length > 3 && (
                  <div
                    onClick={() => setShowAllHistory(false)}
                    style={{ fontSize: 12, color: T.textMuted, cursor: 'pointer', textAlign: 'center', padding: '10px 0', marginTop: 4 }}
                  >
                    Mostrar menos
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── STATE 2 & 3: Plan View ── */}
        {activePlan && !loading && (
          <div style={{ animation: 'crossfadeIn 0.5s ease' }}>
            {/* Header */}
            <div class="orch-card">
              {/* Title + category */}
              <div style={{ fontSize: 18, fontWeight: 600, color: T.text, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {activePlan.title}
                {activePlan.category && (
                  <span class={`orch-category-badge ${activePlan.category}`}>
                    {activePlan.category === 'programming' ? 'Programming' : activePlan.category === 'analysis' ? 'Analysis' : activePlan.category === 'content' ? 'Content' : 'Generic'}
                  </span>
                )}
              </div>
              {/* "O que voce precisa" section — disabled, content in researched detail blocks */}
              {false && (() => {
                const pj = (() => { try { return typeof activePlan.plan_json === 'string' ? JSON.parse(activePlan.plan_json) : (activePlan.plan_json || {}); } catch { return {}; } })();
                const techStack: string[] = pj.tech_stack || pj.techStack || [];
                const considerations: string[] = pj.key_considerations || pj.keyConsiderations || [];
                const brains = activePlan.brains || [];
                const hasContent = brains.length > 0 || techStack.length > 0 || considerations.length > 0;
                if (!hasContent) return null;
                return (
                  <div style={{ marginTop: 4, marginBottom: 12, padding: 16, background: T.bg, borderRadius: 10, border: `1px solid ${T.accentBg}` }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14 }}>O que voce precisa pra executar</div>

                    {brains.length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div style={{ fontSize: 11, color: T.accent, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>Profissionais / Agentes IA</div>
                        </div>
                        <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 10, lineHeight: 1.5 }}>
                          Clique em cada funcao pra escolher qual agente vai assumir o papel.
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {brains.map((b: any) => {
                            const assigned = roleAssignments[b.key];
                            const assignedAgent = agents.find(a => a.id === assigned);
                            const isOpen = expandedRole === b.key;
                            // Only show agents that were escalated in the estimate stage
                            const escalatedIds: string[] = (() => { try { const p = typeof activePlan.plan_json === 'string' ? JSON.parse(activePlan.plan_json) : (activePlan.plan_json || {}); return p.selectedRunnerIds || []; } catch { return []; } })();
                            const teamAgents = escalatedIds.length > 0 ? agents.filter(a => escalatedIds.includes(a.id)) : agents;
                            return (
                              <div key={b.key} style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${assigned ? (b.color || T.accent) + '33' : T.border}`, transition: 'border-color 0.2s' }}>
                                {/* Card header — always visible */}
                                <div onClick={() => setExpandedRole(isOpen ? null : b.key)} style={{
                                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer',
                                  background: assigned ? (b.color || T.accent) + '0a' : T.bg, transition: 'background 0.2s',
                                }}>
                                  <span style={{ fontSize: 20 }}>{b.emoji}</span>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: b.color || T.text }}>{b.label}</div>
                                    {assignedAgent ? (
                                      <div style={{ fontSize: 11, color: T.success, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <div style={{ width: 5, height: 5, borderRadius: 3, background: assignedAgent.status === 'online' ? T.success : T.textMuted }} />
                                        {assignedAgent.agent_name}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Nenhum agente selecionado</div>
                                    )}
                                  </div>
                                  <span style={{ fontSize: 12, color: T.border, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }}>&#9660;</span>
                                </div>
                                {/* Expanded agent list */}
                                {isOpen && (
                                  <div style={{ background: T.bg, borderTop: `1px solid ${T.bgCard}`, padding: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {teamAgents.length === 0 && (
                                      <div style={{ fontSize: 11, color: T.textMuted, padding: 10, textAlign: 'center' }}>Nenhum agente escalado</div>
                                    )}
                                    {teamAgents.filter(a => a.status === 'online').map(a => (
                                      <div key={a.id} onClick={() => { setRoleAssignments(prev => ({ ...prev, [b.key]: a.id })); setExpandedRole(null); }}
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 6, cursor: 'pointer', background: assigned === a.id ? T.successBg : 'transparent', transition: 'background 0.15s' }}
                                        onMouseEnter={(e: any) => { if (assigned !== a.id) e.currentTarget.style.background = T.bgCard; }}
                                        onMouseLeave={(e: any) => { if (assigned !== a.id) e.currentTarget.style.background = 'transparent'; }}>
                                        <div style={{ width: 8, height: 8, borderRadius: 4, background: T.success, flexShrink: 0 }} />
                                        <div style={{ flex: 1 }}>
                                          <div style={{ fontSize: 12, fontWeight: 500, color: T.text }}>{a.agent_name}</div>
                                          {a.model && <div style={{ fontSize: 10, color: T.textMuted }}>{a.model}</div>}
                                        </div>
                                        {assigned === a.id && <span style={{ fontSize: 10, color: T.success, fontWeight: 600 }}>Atribuido</span>}
                                      </div>
                                    ))}
                                    {teamAgents.filter(a => a.status !== 'online').length > 0 && (
                                      <div style={{ fontSize: 10, color: T.textMuted, padding: '4px 12px', marginTop: 2 }}>Offline</div>
                                    )}
                                    {teamAgents.filter(a => a.status !== 'online').map(a => (
                                      <div key={a.id} onClick={() => { setRoleAssignments(prev => ({ ...prev, [b.key]: a.id })); setExpandedRole(null); }}
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 6, cursor: 'pointer', opacity: 0.5 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: 4, background: T.textMuted, flexShrink: 0 }} />
                                        <div style={{ fontSize: 12, color: T.textSecondary }}>{a.agent_name}</div>
                                        {assigned === a.id && <span style={{ fontSize: 10, color: T.accent, marginLeft: 'auto' }}>Atribuido</span>}
                                      </div>
                                    ))}
                                    {assigned && (
                                      <div onClick={() => { setRoleAssignments(prev => { const n = { ...prev }; delete n[b.key]; return n; }); setExpandedRole(null); }}
                                        style={{ fontSize: 11, color: T.danger, padding: '6px 12px', cursor: 'pointer', textAlign: 'center', borderTop: `1px solid ${T.bgCard}`, marginTop: 4 }}>
                                        Remover
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {/* Auto-assign button */}
                        <Button
                          variant="secondary"
                          onClick={async () => {
                            if (autoAssigning || agents.length === 0) return;
                            setAutoAssigning(true);
                            try {
                              const res: any = await api.post(`/orchestrator/plans/${activePlan.id}/auto-assign`, { brains: brains.map((b: any) => b.key) });
                              if (res?.assignments) setRoleAssignments(res.assignments);
                            } catch { /* silent */ }
                            finally { setAutoAssigning(false); }
                          }}
                          disabled={autoAssigning || agents.length === 0}
                          fullWidth
                          loading={autoAssigning}
                          style={{ marginTop: 10, padding: '10px 16px', borderRadius: 8, border: `1px dashed ${T.accentBg}`, background: T.accentBg, color: `#7c5cfc`, fontSize: 12 }}
                        >
                          {autoAssigning ? 'Analisando agentes...' : 'Atribuir automaticamente (usa tokens)'}
                        </Button>
                        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4, textAlign: 'center' }}>
                          Analisa o perfil de cada agente e atribui a melhor opcao para cada funcao
                        </div>
                      </div>
                    )}

                    {techStack.length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, color: T.accent, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>Ferramentas e tecnologias</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {techStack.map((t: string, i: number) => (
                            <span key={i} class="orch-tool-chip">{t}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {considerations.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, color: T.warning, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>Pontos de atencao</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {considerations.map((c: string, i: number) => (
                            <div key={i} style={{ fontSize: 12, color: T.text, lineHeight: 1.5, display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 10px', background: T.warningBg, borderRadius: 6, border: `1px solid ${T.warningBg}` }}>
                              <span style={{ color: T.warning, flexShrink: 0, marginTop: 1 }}>!</span>
                              <span>{c}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div class="orch-status-bar">
                <span class={`orch-status-pill ${activePlan.status}`}>
                  {{ estimated: 'Pesquisado', researched: 'Pesquisado', generating: 'Gerando', ready: 'Pronto', executing: 'Executando', completed: 'Concluído', failed: 'Falhou', cancelled: 'Cancelado' }[activePlan.status] || activePlan.status}
                </span>
                <span>{activePlan.total_tasks} tarefas</span>
                {activePlan.estimated_duration_min && <span>~{activePlan.estimated_duration_min}min estimado</span>}
                {activePlan.status === 'executing' && <span>{elapsed}min decorridos</span>}
              </div>

              {/* Progress bar */}
              {(activePlan.status === 'executing' || activePlan.status === 'completed' || activePlan.status === 'failed') && (
                <div class="orch-progress-bar">
                  <div class="orch-progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
              )}

              {/* Action buttons (non-estimated states, excluding ready) */}
              {activePlan.status !== 'estimated' && activePlan.status !== 'researched' && activePlan.status !== 'ready' && (
                <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                  {activePlan.status === 'executing' && (
                    <Button variant="danger" onClick={cancelPlan}>Cancelar</Button>
                  )}
                  <Button variant="ghost" onClick={() => { setActivePlan(null); setTasks([]); setEvents([]); setChannels([]); setChannelMessages([]); setExpandedTeamId(null); }}>Voltar</Button>
                </div>
              )}
              {(activePlan.status === 'estimated' || activePlan.status === 'researched') && (
                <div style={{ marginTop: 8 }}>
                  <Button variant="ghost" onClick={() => { setActivePlan(null); setTasks([]); setEvents([]); setChannels([]); setChannelMessages([]); setExpandedTeamId(null); }}>← Voltar</Button>
                </div>
              )}
            </div>

            {/* Stats summary (ready state) */}
            {activePlan.status === 'ready' && (() => {
              const pjStats = (() => { try { return typeof activePlan.plan_json === 'string' ? JSON.parse(activePlan.plan_json) : (activePlan.plan_json || {}); } catch { return {}; } })();
              const estCost = pjStats.estimated_cost_usd || 0;
              const estPhases = pjStats.estimated_phases || pjStats.phases_preview?.length || 0;
              const estTime = pjStats.estimated_total_min || activePlan.estimated_duration_min || 0;
              return (
                <div class="orch-card" style={{ animation: 'fadeInUp 0.2s ease' }}>
                  {pjStats.summary && <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.6, marginBottom: 16 }}>{pjStats.summary}</div>}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                    {[
                      { val: activePlan.total_tasks || tasks.length || '?', label: 'Tarefas', color: T.accent },
                      { val: estPhases || '?', label: 'Fases', color: T.warning },
                      { val: `$${estCost.toFixed(2)}`, label: 'Custo est.', color: T.success },
                      { val: estTime ? (estTime < 60 ? `${estTime}m` : `${Math.round(estTime / 60)}h`) : '?', label: 'Tempo est.', color: T.primary },
                    ].map((s, i) => (
                      <div key={i} style={{ textAlign: 'center', padding: '12px 8px', background: `${s.color}0d`, borderRadius: 10, border: `1px solid ${s.color}22` }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.val}</div>
                        <div style={{ fontSize: 10, color: T.textSecondary, marginTop: 2 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Team escalated (shown on ready/executing/completed) */}
            {activePlan.status !== 'estimated' && activePlan.status !== 'researched' && activePlan.status !== 'generating' && (() => {
              const pjTeam = (() => { try { return typeof activePlan.plan_json === 'string' ? JSON.parse(activePlan.plan_json) : (activePlan.plan_json || {}); } catch { return {}; } })();
              const escalatedIds: string[] = pjTeam.selectedRunnerIds || [];
              const escalatedAgents = agents.filter(a => escalatedIds.includes(a.id));
              if (escalatedAgents.length === 0) return null;
              return (
                <div class="orch-card" style={{ animation: 'fadeInUp 0.25s ease' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Time escalado ({escalatedAgents.length})</div>
                    <span style={{ fontSize: 11, color: escalatedAgents.filter(a => a.status === 'online').length === escalatedAgents.length ? T.success : T.warning }}>
                      {escalatedAgents.filter(a => a.status === 'online').length} online
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                    {escalatedAgents.map(ag => (
                      <div key={ag.id} style={{
                        background: ag.status === 'online' ? T.successBg : T.bgCard,
                        border: `1px solid ${ag.status === 'online' ? T.successBg : T.border}`,
                        borderRadius: 10, padding: 14, transition: 'all 0.2s',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: 8, background: T.accentBg,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 14, color: T.accent, fontWeight: 600,
                          }}>
                            {(ag.agent_name || 'A').charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 500, color: T.text }}>{ag.agent_name}</div>
                            {ag.model && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>{ag.model}</div>}
                          </div>
                          <span style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 4,
                            color: ag.status === 'online' ? T.success : T.textSecondary,
                            background: ag.status === 'online' ? T.successBg : T.bgCardHover,
                          }}>
                            {ag.status === 'online' ? 'Online' : 'Offline'}
                          </span>
                        </div>
                        {/* Capabilities summary */}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {requiredTools.filter(t => t.has_credentials && t.credentials?.some((c: any) => c.runner_id === ag.id)).map(t => (
                            <span key={t.provider} style={{ fontSize: 10, color: T.success, background: T.successBg, padding: '2px 6px', borderRadius: 4 }}>{t.label}</span>
                          ))}
                          {requiredTools.filter(t => !t.has_credentials || !t.credentials?.some((c: any) => c.runner_id === ag.id)).length > 0 &&
                            requiredTools.filter(t => !t.has_credentials || !t.credentials?.some((c: any) => c.runner_id === ag.id)).map(t => (
                              <span key={t.provider} style={{ fontSize: 10, color: T.danger, background: T.dangerBg, padding: '2px 6px', borderRadius: 4 }}>Sem {t.label}</span>
                            ))
                          }
                          {requiredTools.length === 0 && (
                            <span style={{ fontSize: 10, color: T.textMuted }}>Nenhuma ferramenta detectada</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ── GENERATING: Real-time generation view ── */}
            {activePlan.status === 'generating' && (() => {
              const genEvents = events.filter(e => e.event_type === 'generation_step');
              const pj = (() => { try { return typeof activePlan.plan_json === 'string' ? JSON.parse(activePlan.plan_json) : (activePlan.plan_json || {}); } catch { return {}; } })();
              const totalPhases = pj.totalPhases || pj.skeleton?.phases?.length || 0;
              const nextPhase = pj.nextPhase || 0;
              const phases = pj.skeleton?.phases || [];
              const progressPctGen = totalPhases > 0 ? Math.round((nextPhase / totalPhases) * 100) : 0;

              return (
                <>
                  {/* Generation progress card */}
                  <div class="orch-card" style={{ animation: 'fadeInUp 0.3s ease', background: `linear-gradient(135deg, ${T.accentBgSubtle} 0%, ${T.warningBgSubtle} 100%)`, border: `1px solid ${T.accentBg}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: T.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span class="orch-pulse-dot" style={{ width: 12, height: 12 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>Construindo seu plano</div>
                        <div style={{ fontSize: 12, color: T.textSecondary }}>
                          {totalPhases > 0
                            ? `Fase ${nextPhase} de ${totalPhases} concluída`
                            : 'Analisando complexidade e estruturando fases...'
                          }
                        </div>
                      </div>
                    </div>

                    {/* Progress bar */}
                    {totalPhases > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.textSecondary, marginBottom: 6 }}>
                          <span>Progresso</span>
                          <span>{progressPctGen}%</span>
                        </div>
                        <div style={{ height: 6, background: T.border, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${progressPctGen}%`, background: 'linear-gradient(90deg, #7c5cfc, #a78bfa)', borderRadius: 3, transition: 'width 0.5s ease' }} />
                        </div>
                      </div>
                    )}

                    {/* Phases list */}
                    {phases.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {phases.map((p: any, i: number) => {
                          const isDone = i < nextPhase;
                          const isCurrent = i === nextPhase;
                          return (
                            <div key={i} style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                              background: isDone ? T.successBg : isCurrent ? T.warningBg : T.bgCard,
                              borderRadius: 8, border: `1px solid ${isDone ? T.successBg : isCurrent ? T.warningBg : T.bgCard}`,
                              transition: 'all 0.3s ease',
                            }}>
                              <div style={{
                                width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600,
                                background: isDone ? T.successBg : isCurrent ? T.warningBg : T.border,
                                color: isDone ? T.success : isCurrent ? T.warning : T.textMuted,
                              }}>
                                {isDone ? '' : i + 1}
                              </div>
                              <span style={{ fontSize: 13, color: isDone ? T.success : isCurrent ? T.warning : T.textSecondary, flex: 1 }}>{p.name}</span>
                              {isCurrent && <span class="orch-pulse-dot" style={{ width: 8, height: 8 }} />}
                              {isDone && <span style={{ fontSize: 11, color: T.success }}>Concluída</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Real-time event log — Terminal style */}
                  <div style={{
                    animation: 'fadeInUp 0.4s ease',
                    background: T.bg,
                    borderRadius: 10,
                    border: `1px solid ${T.accentBg}`,
                    overflow: 'hidden',
                    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Consolas', monospace",
                  }}>
                    {/* Terminal header bar */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                      background: `linear-gradient(135deg, ${T.accentBg}, ${T.accentBgSubtle})`,
                      borderBottom: `1px solid ${T.accentBg}`,
                    }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 5, background: T.danger }} />
                        <div style={{ width: 10, height: 10, borderRadius: 5, background: T.warning }} />
                        <div style={{ width: 10, height: 10, borderRadius: 5, background: T.success }} />
                      </div>
                      <span style={{ fontSize: 11, color: T.accent, fontWeight: 600, letterSpacing: 1, marginLeft: 8 }}>ORCHESTRATOR</span>
                      <span style={{ fontSize: 10, color: T.textMuted, marginLeft: 'auto' }}>live</span>
                      <div style={{ width: 6, height: 6, borderRadius: 3, background: T.success, animation: 'pulse 2s ease-in-out infinite' }} />
                    </div>
                    {/* Terminal body */}
                    <div ref={(el: HTMLDivElement | null) => { if (el) el.scrollTop = el.scrollHeight; }} style={{
                      display: 'flex', flexDirection: 'column', gap: 0,
                      maxHeight: 340, overflowY: 'auto', padding: '10px 0',
                      scrollBehavior: 'smooth',
                    }}>
                      {genEvents.length === 0 && (
                        <div style={{ fontSize: 12, color: '#7c5cfc88', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: T.accent }}>$</span> Aguardando...
                          <span style={{ animation: 'blink 1s step-end infinite', color: T.accent }}>_</span>
                        </div>
                      )}
                      {genEvents.map((ev: any, i: number) => {
                        const data = typeof ev.event_data === 'string' ? JSON.parse(ev.event_data) : ev.event_data;
                        const msg = (data.message || '').replace(/sdd_\w+_prompt/g, 'prompt especializado').replace(/USE_CODE_DEFAULT/g, 'padrão');
                        const isError = data.step === 'error';
                        const isSuccess = msg.includes('Esqueleto:') || msg.includes('Fase ') || msg.includes('Complexidade:') || msg.includes('Plano finalizado') || msg.includes('fases definidas') || msg.includes('tasks geradas');
                        const isProgress = msg.includes('Gerando') || msg.includes('Analisando') || msg.includes('Finalizando');
                        const isLast = i === genEvents.length - 1;
                        return (
                          <div key={i} style={{
                            display: 'flex', alignItems: 'flex-start', gap: 0,
                            padding: '4px 16px',
                            background: isError ? T.dangerBg : isLast ? T.accentBg : 'transparent',
                            transition: 'background 0.3s',
                          }}>
                            <span style={{
                              fontSize: 10, color: T.border, minWidth: 62, paddingTop: 2, flexShrink: 0,
                            }}>
                              {new Date(ev.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                            <span style={{
                              fontSize: 11, width: 14, flexShrink: 0, paddingTop: 1,
                              color: isError ? T.danger : isSuccess ? T.success : isProgress ? T.accent : T.textMuted,
                            }}>
                              {isError ? 'x' : isSuccess ? '>' : isProgress ? '~' : '-'}
                            </span>
                            <span style={{
                              fontSize: 12, lineHeight: 1.6,
                              color: isError ? T.danger : isSuccess ? '#c8ffc8' : isProgress ? T.textSecondary : T.textSecondary,
                              wordBreak: 'break-word',
                            }}>{msg}{isLast && !isError && !isSuccess && <span style={{ color: T.accent, animation: 'blink 1s step-end infinite' }}> _</span>}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}

            {/* ── RESEARCHED PLAN DETAIL ── */}
            {(activePlan.status === 'estimated' || activePlan.status === 'researched') && (() => {
              const est = (() => { try { return typeof activePlan.plan_json === 'string' ? JSON.parse(activePlan.plan_json) : (activePlan.plan_json || {}); } catch { return {}; } })();
              const onlineAgentsList = agents.filter(a => a.status === 'online');
              const offlineAgentsList = agents.filter(a => a.status !== 'online');
              const [showDetails, setShowDetails] = useState(false);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                  {/* ── SUMMARY CARD ── */}
                  <div class="orch-card" style={{ animation: 'fadeInUp 0.2s ease', padding: '20px 24px' }}>
                    {est.summary && <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.6, marginBottom: 16 }}>{est.summary}</div>}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                      {[
                        { val: est.estimated_tasks || activePlan.total_tasks || '?', label: 'Tarefas', color: T.accent },
                        { val: est.estimated_phases || est.phases_preview?.length || '?', label: 'Fases', color: T.warning },
                        { val: `$${(est.estimated_cost_usd || 0).toFixed(2)}`, label: 'Custo est.', color: T.success },
                        { val: (est.estimated_total_min || activePlan.estimated_duration_min) ? ((est.estimated_total_min || activePlan.estimated_duration_min) < 60 ? `${est.estimated_total_min || activePlan.estimated_duration_min}m` : `${Math.round((est.estimated_total_min || activePlan.estimated_duration_min) / 60)}h`) : '?', label: 'Tempo est.', color: T.primary },
                      ].map((s, i) => (
                        <div key={i} style={{ textAlign: 'center', padding: '12px 8px', background: `${s.color}0d`, borderRadius: 10, border: `1px solid ${s.color}22` }}>
                          <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.val}</div>
                          <div style={{ fontSize: 10, color: T.textSecondary, marginTop: 2 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                    {/* Phases inline */}
                    {est.phases_preview && est.phases_preview.length > 0 && (
                      <div style={{ marginTop: 16, borderTop: `1px solid ${T.bgCard}`, paddingTop: 12 }}>
                        {est.phases_preview.map((p: any, i: number) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < est.phases_preview.length - 1 ? `1px solid ${T.bgCard}` : 'none' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 20, height: 20, borderRadius: '50%', background: T.accentBg, color: T.accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>{i + 1}</span>
                              <span style={{ fontSize: 12, color: T.text }}>{p.name}</span>
                            </div>
                            <span style={{ fontSize: 11, color: T.textMuted }}>{p.tasks_count} tasks · ~{p.estimated_min}min</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── AGENTS (compact grid) ── */}
                  <div class="orch-card" style={{ animation: 'fadeInUp 0.3s ease' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Selecione os agentes</div>
                      <span style={{ fontSize: 11, color: selectedAgents.length > 0 ? T.accent : T.textMuted }}>{selectedAgents.length} selecionado{selectedAgents.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                      {onlineAgentsList.map(a => {
                        const isSel = selectedAgents.includes(a.id);
                        return (
                          <div key={a.id} onClick={() => toggleAgent(a.id)} style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                            background: isSel ? T.accentBg : T.bg,
                            border: `1px solid ${isSel ? '#7c5cfc44' : T.border}`, transition: 'all 0.15s',
                          }}>
                            <div style={{ width: 8, height: 8, borderRadius: 4, background: T.success, flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: T.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.agent_name}</span>
                            <div style={{ width: 18, height: 18, borderRadius: 4, background: isSel ? T.accent : 'transparent', border: `1px solid ${isSel ? T.accent : T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                              {isSel && <span style={{ color: T.text, fontSize: 10 }}></span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {selectedAgents.length === 0 && onlineAgentsList.length > 0 && (
                      <div style={{ fontSize: 12, color: T.warning, marginTop: 10 }}>Selecione pelo menos 1 agente para continuar</div>
                    )}
                    {offlineAgentsList.length > 0 && (
                      <div style={{ marginTop: 10, fontSize: 11, color: T.textMuted, cursor: 'pointer' }} onClick={() => setShowOffline(!showOffline)}>
                        {showOffline ? '▲' : '▼'} {offlineAgentsList.length} offline
                        {showOffline && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginTop: 8 }}>
                            {offlineAgentsList.map(a => {
                              const isSel = selectedAgents.includes(a.id);
                              return (
                                <div key={a.id} onClick={() => toggleAgent(a.id)} style={{
                                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', opacity: 0.5,
                                  background: isSel ? T.accentBg : T.bg,
                                  border: `1px solid ${isSel ? '#7c5cfc44' : T.border}`,
                                }}>
                                  <div style={{ width: 8, height: 8, borderRadius: 4, background: T.textMuted, flexShrink: 0 }} />
                                  <span style={{ fontSize: 12, color: T.textSecondary, flex: 1 }}>{a.agent_name}</span>
                                  <div style={{ width: 18, height: 18, borderRadius: 4, background: isSel ? T.accent : 'transparent', border: `1px solid ${isSel ? T.accent : T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {isSel && <span style={{ color: T.text, fontSize: 10 }}></span>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── MODELS (compact) ── */}
                  <div class="orch-card" style={{ animation: 'fadeInUp 0.35s ease' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: T.textSecondary }}>Modelo LLM:</span>
                      <Select
                        value={selectedModel}
                        onChange={setSelectedModel}
                        options={availableModels.length > 0 ? availableModels.map(m => ({ value: m.id, label: m.name })) : [{ value: 'auto', label: 'Auto (Smart Routing)' }]}
                        style={{ flex: 1, height: 36, fontSize: 12, padding: '0 10px' }}
                      />
                    </div>
                    <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }}>LLMs para execucao ({selectedExecModels.length}/{availableModels.length}):</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {availableModels.map(m => {
                        const isSel = selectedExecModels.includes(m.id);
                        return (
                          <div key={m.id} onClick={() => setSelectedExecModels(prev => isSel ? prev.filter(id => id !== m.id) : [...prev, m.id])} style={{
                            display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, transition: 'all 0.15s',
                            background: isSel ? T.accentBg : T.bgCard,
                            border: `1px solid ${isSel ? T.accentBg : T.border}`,
                            color: isSel ? T.text : T.textSecondary,
                          }}>
                            <div style={{ width: 12, height: 12, borderRadius: 3, border: `2px solid ${isSel ? T.accent : T.borderHover}`, background: isSel ? T.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {isSel && <span style={{ color: T.text, fontSize: 7, lineHeight: 1 }}></span>}
                            </div>
                            {m.name}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── TECH STACK + CONSIDERATIONS ── */}
                  {(() => {
                    const techStack: string[] = est.tech_stack || [];
                    const considerations: string[] = est.key_considerations || [];
                    if (techStack.length === 0 && considerations.length === 0) return null;
                    return (
                      <div class="orch-card" style={{ animation: 'fadeInUp 0.4s ease' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>Requisitos do projeto</div>
                        {techStack.length > 0 && (
                          <div style={{ marginBottom: considerations.length > 0 ? 12 : 0 }}>
                            <div style={{ fontSize: 10, color: T.accent, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>Ferramentas e tecnologias</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {techStack.map((t: string, i: number) => (
                                <span key={i} style={{ fontSize: 11, color: T.text, background: T.accentBg, border: `1px solid ${T.accentBg}`, borderRadius: 6, padding: '3px 10px' }}>{t}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {considerations.length > 0 && (
                          <div>
                            <div style={{ fontSize: 10, color: T.warning, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>Pontos de atencao</div>
                            {considerations.map((c: string, i: number) => (
                              <div key={i} style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.5, padding: '4px 0', display: 'flex', gap: 6 }}>
                                <span style={{ color: T.warning, flexShrink: 0 }}>·</span>
                                <span>{c}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── CTA: CREATE PLAN ── */}
                  <div style={{ animation: 'fadeInUp 0.45s ease' }}>
                    <Button variant="primary" onClick={createPlanFromEstimate} disabled={creating || selectedAgents.length === 0} fullWidth style={{
                      padding: '14px 20px', fontSize: 16, borderRadius: 10,
                    }}>
                      {creating ? 'Criando plano...' : 'Criar Plano'}
                    </Button>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                      <div style={{ fontSize: 11, color: T.textMuted }}>
                        Custo estimado: <strong style={{ color: T.accent }}>${(est.estimated_cost_usd || 0).toFixed(2)}</strong> em creditos
                      </div>
                      <Button variant="danger" size="xs" onClick={deleteEstimate} style={{ padding: '6px 14px', fontSize: 11 }}>Excluir pesquisa</Button>
                    </div>
                    {selectedAgents.length === 0 && agents.length > 0 && (
                      <div style={{ fontSize: 12, color: T.warning, marginTop: 6, textAlign: 'center' }}>
                        Selecione pelo menos 1 agente acima
                      </div>
                    )}
                  </div>

                  {/* ── DETAILS (expandable) ── */}
                  {((est.research_report) || (est.risks && est.risks.length > 0) || (est.deliverables && est.deliverables.length > 0)) && (
                    <div>
                      <div onClick={() => setShowDetails(!showDetails)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 0' }}>
                        <span style={{ fontSize: 13, color: T.textMuted, transition: 'transform 0.2s', transform: showDetails ? 'rotate(90deg)' : 'none' }}></span>
                        <span style={{ fontSize: 13, color: T.textSecondary }}>Detalhes da analise</span>
                      </div>
                      {showDetails && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          {est.research_report && (
                            <div class="orch-card" style={{ padding: 16 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Pesquisa</div>
                              <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>{est.research_report}</div>
                            </div>
                          )}
                          {est.risks && est.risks.length > 0 && (
                            <div class="orch-card" style={{ padding: 16 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Riscos</div>
                              <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {est.risks.map((r: string, i: number) => <li key={i} style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.6, marginBottom: 2 }}>{r}</li>)}
                              </ul>
                            </div>
                          )}
                          {est.deliverables && est.deliverables.length > 0 && (
                            <div class="orch-card" style={{ padding: 16 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Entregas esperadas</div>
                              <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {est.deliverables.map((d: string, i: number) => <li key={i} style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.6, marginBottom: 2 }}>{d}</li>)}
                              </ul>
                            </div>
                          )}
                          <div class="orch-card" style={{ padding: 16 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Prompt original</div>
                            <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: 150, overflow: 'auto' }}>{activePlan.original_prompt}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Required Tools (shown when plan is ready) */}
            {activePlan.status === 'ready' && requiredTools.length > 0 && (
              <div class="orch-card" style={{ animation: 'fadeInUp 0.3s ease' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                    Ferramentas Necessarias ({requiredTools.length})
                  </div>
                  <span style={{ fontSize: 12, color: T.textMuted }}>
                    {requiredTools.filter(t => t.has_credentials).length} com credenciais configuradas
                  </span>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <a
                    onClick={(e: any) => { e.preventDefault(); window.open('/integrations', '_blank'); }}
                    style={{ fontSize: 12, color: T.accent, cursor: 'pointer', textDecoration: 'none' }}
                  >
                    Como configurar integracoes?
                  </a>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                  {requiredTools.map(tool => {
                    const sources: string[] = [];
                    if (tool.customer_has_credentials) sources.push('Minha conta');
                    for (const c of (tool.credentials || [])) sources.push(c.agent_name || 'Agent');
                    return (
                    <div
                      key={tool.provider}
                      style={{
                        background: tool.has_credentials ? T.bgCard : T.dangerBg,
                        border: `1px solid ${tool.has_credentials ? T.border : T.dangerBg}`,
                        borderRadius: 10, padding: 14,
                      }}
                    >
                      {/* Header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: T.border,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {tool.svg_path ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill={tool.color || T.text}>
                              <path d={tool.svg_path} />
                            </svg>
                          ) : <span style={{ fontSize: 16 }}>{'⊡'}</span>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 500, color: T.text }}>{tool.label}</div>
                          <div style={{ fontSize: 11, color: T.textMuted }}>{tool.category}</div>
                        </div>
                        {tool.has_credentials ? (
                          <span style={{ fontSize: 11, color: T.success, background: T.successBg, padding: '2px 8px', borderRadius: 4 }}>Configurado</span>
                        ) : (
                          <span style={{ fontSize: 11, color: T.danger, background: T.dangerBg, padding: '2px 8px', borderRadius: 4 }}>Sem credenciais</span>
                        )}
                      </div>

                      {/* Account preview — masked credentials */}
                      {(tool as any).account_keys?.length > 0 && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                          {Object.entries((tool as any).account_preview || {}).map(([key, val]: [string, any]) => (
                            <span key={key} style={{
                              fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: T.textMuted,
                              padding: '1px 6px', background: T.bgCard, borderRadius: 4,
                            }}>{key}: {val}</span>
                          ))}
                        </div>
                      )}

                      {/* Source info — where credentials come from */}
                      {sources.length > 0 && (
                        <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                          <span style={{ color: T.textMuted }}>Fonte:</span>
                          {sources.map((s, i) => (
                            <span key={i} style={{ background: T.accentBg, border: `1px solid ${T.accentBg}`, padding: '1px 6px', borderRadius: 4, color: T.textSecondary }}>{s}</span>
                          ))}
                        </div>
                      )}

                      {/* Actions — always visible */}
                      {credentialFormOpen === tool.provider ? (
                        <div style={{ marginTop: 4 }}>
                          {(() => {
                            // Use fields from enriched tools endpoint (D1 catalog), fallback to INTEGRATIONS
                            const toolFields = (tool as any).fields || [];
                            const integ = toolFields.length > 0 ? { fields: toolFields, howToGet: (tool as any).how_to_get } : INTEGRATIONS.find(i => i.id === tool.provider);
                            if (!integ || !integ.fields?.length) return <div style={{ fontSize: 12, color: T.textSecondary }}>Integracao nao encontrada</div>;
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {integ.fields.filter((f: any) => f.required !== false).map((field: any) => (
                                  <div key={field.key}>
                                    <label style={{ fontSize: 11, color: T.textSecondary, marginBottom: 3, display: 'block' }}>
                                      {field.label}
                                      {field.required && <span style={{ color: T.danger }}> *</span>}
                                    </label>
                                    <input
                                      type={field.type === 'password' ? 'password' : 'text'}
                                      placeholder={field.placeholder}
                                      value={credentialValues[field.key] || ''}
                                      onInput={(e: any) => setCredentialValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                                      style={{
                                        width: '100%', height: 34, background: T.bg,
                                        border: `1px solid ${T.borderHover}`, borderRadius: 6,
                                        color: T.text, fontSize: 12, padding: '0 10px',
                                        outline: 'none', boxSizing: 'border-box' as const,
                                      }}
                                    />
                                  </div>
                                ))}
                                {integ.fields.some((f: any) => f.required === false || f.required === undefined) && (
                                  <details style={{ marginTop: 2 }}>
                                    <summary style={{ fontSize: 11, color: T.textMuted, cursor: 'pointer' }}>Campos opcionais</summary>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                                      {integ.fields.filter((f: any) => !f.required).map((field: any) => (
                                        <div key={field.key}>
                                          <label style={{ fontSize: 11, color: T.textMuted, marginBottom: 3, display: 'block' }}>{field.label}</label>
                                          <input
                                            type={field.type === 'password' ? 'password' : 'text'}
                                            placeholder={field.placeholder}
                                            value={credentialValues[field.key] || ''}
                                            onInput={(e: any) => setCredentialValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                                            style={{
                                              width: '100%', height: 34, background: T.bg,
                                              border: `1px solid ${T.bgInputHover}`, borderRadius: 6,
                                              color: T.text, fontSize: 12, padding: '0 10px',
                                              outline: 'none', boxSizing: 'border-box' as const,
                                            }}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                )}
                                <div style={{ marginTop: 4 }}>
                                  <label style={{ fontSize: 11, color: T.textSecondary, marginBottom: 4, display: 'block' }}>Salvar em:</label>
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    <div
                                      onClick={() => setCredentialTarget('account')}
                                      style={{
                                        padding: '6px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                                        background: credentialTarget === 'account' ? T.accentBg : T.bgCard,
                                        border: `1px solid ${credentialTarget === 'account' ? T.accentBg : T.border}`,
                                        color: credentialTarget === 'account' ? T.text : T.textSecondary,
                                      }}
                                    >Minha conta</div>
                                    {agents.filter(a => selectedAgents.includes(a.id) || ((() => { try { const p = typeof activePlan?.plan_json === "string" ? JSON.parse(activePlan.plan_json) : (activePlan?.plan_json || {}); return (p.selectedRunnerIds || []).includes(a.id); } catch { return false; } })())).map(a => (
                                      <div
                                        key={a.id}
                                        onClick={() => setCredentialTarget(a.id)}
                                        style={{
                                          padding: '6px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                                          background: credentialTarget === a.id ? T.accentBg : T.bgCard,
                                          border: `1px solid ${credentialTarget === a.id ? T.accentBg : T.border}`,
                                          color: credentialTarget === a.id ? T.text : T.textSecondary,
                                        }}
                                      >{a.agent_name}</div>
                                    ))}
                                  </div>
                                </div>
                                {integ.howToGet && (
                                  <details style={{ marginTop: 4 }}>
                                    <summary style={{ fontSize: 11, color: T.accent, cursor: 'pointer' }}>Como conseguir essas credenciais?</summary>
                                    <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.6, marginTop: 6, padding: '8px 10px', background: T.accentBg, borderRadius: 6, whiteSpace: 'pre-wrap' }}>
                                      {integ.howToGet.replace(/\*\*/g, '').replace(/`/g, '')}
                                    </div>
                                  </details>
                                )}
                                {credentialMsg && (
                                  <div style={{ fontSize: 11, color: credentialMsg.includes('sucesso') ? T.success : T.danger }}>{credentialMsg}</div>
                                )}
                                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => saveCredential(tool.provider)}
                                    disabled={credentialSaving}
                                    loading={credentialSaving}
                                    style={{ flex: 1, borderRadius: 6, fontSize: 12, padding: '8px 14px', background: T.accent }}
                                  >{credentialSaving ? 'Salvando...' : 'Salvar credencial'}</Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setCredentialFormOpen(null); setCredentialValues({}); setCredentialMsg(''); }}
                                    style={{ borderRadius: 6, fontSize: 12, padding: '8px 14px', border: `1px solid ${T.borderHover}`, color: T.textSecondary }}
                                  >Cancelar</Button>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          {tool.provider === 'github' && !tool.has_credentials && (
                            <div
                              onClick={() => { window.location.href = '/api/v1/integrations/github/oauth'; }}
                              style={{
                                padding: '6px 12px', fontSize: 11, color: T.text, background: T.bgSecondary,
                                borderRadius: 6, cursor: 'pointer', fontWeight: 500,
                                display: 'flex', alignItems: 'center', gap: 6,
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
                              Conectar com GitHub
                            </div>
                          )}
                          <div
                            onClick={() => {
                              setCredentialFormOpen(tool.provider);
                              setCredentialValues({});
                              setCredentialTarget('account');
                              setCredentialMsg('');
                            }}
                            style={{
                              padding: '6px 0', fontSize: 11, color: T.accent,
                              cursor: 'pointer', fontWeight: 500,
                            }}
                          >
                            {tool.has_credentials ? 'Trocar ou adicionar credencial' : (tool.provider === 'github' ? 'Ou colar token manualmente' : 'Configurar credencial')}
                          </div>
                        </div>
                      )}
                    </div>
                  );})}
                </div>
              </div>
            )}
            {activePlan.status === 'ready' && loadingTools && (
              <div class="orch-card" style={{ textAlign: 'center', color: T.textMuted, padding: 20 }}>
                Analisando ferramentas necessárias...
              </div>
            )}

            {/* DAG (hub mode) or Team views */}
            {(!activePlan.mode || activePlan.mode === 'hub') && tasks.length > 0 && (
              <div class="orch-card" style={{ animation: 'fadeInUp 0.3s ease' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>Fluxo de trabalho</div>
                <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>Ordem de execucao das tarefas. Cada caixa e uma etapa — as setas mostram a sequencia e dependencias entre elas.</div>
                <DAGView tasks={tasks} />
              </div>
            )}

            {activePlan.mode === 'team' && channels.length > 0 && channels[0] && (
              <TeamChatView
                channel={channels[0]!}
                messages={channelMessages.filter(m => m.channel_id === channels[0]!.id)}
                onNudge={() => nudgeChannel(channels[0]!.id)}
              />
            )}

            {activePlan.mode === 'hybrid' && channels.length > 0 && (
              <TeamDAGView
                channels={channels}
                messages={channelMessages}
                onExpand={setExpandedTeamId}
                expandedId={expandedTeamId}
                onNudge={nudgeChannel}
              />
            )}

            {/* Cancelled/Error plan info — show what was generated */}
            {(activePlan.status === 'cancelled' || activePlan.status === 'error' || activePlan.status === 'failed') && (() => {
              const pj = (() => { try { return typeof activePlan.plan_json === 'string' ? JSON.parse(activePlan.plan_json) : (activePlan.plan_json || {}); } catch { return {}; } })();
              const skeleton = pj.skeleton;
              const phases = skeleton?.phases || pj.phases_preview || [];
              const completedTasks = pj.completedTasks || [];
              const summary = pj.summary || skeleton?.summary || '';
              const hasInfo = phases.length > 0 || summary || completedTasks.length > 0;
              if (!hasInfo) return null;
              return (
                <>
                  {summary && (
                    <div class="orch-card" style={{ borderLeft: `3px solid ${T.bgHover}` }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: T.textSecondary, marginBottom: 6 }}>Resumo do Projeto</div>
                      <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.6 }}>{summary}</div>
                    </div>
                  )}
                  {phases.length > 0 && (
                    <div class="orch-card" style={{ borderLeft: `3px solid ${T.bgHover}` }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: T.textSecondary, marginBottom: 10 }}>Fases planejadas ({phases.length})</div>
                      {phases.map((p: any, i: number) => {
                        const isDone = pj.nextPhase ? i < pj.nextPhase : false;
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 4, background: T.bgCard, borderRadius: 6 }}>
                            <div style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, background: isDone ? T.successBg : T.border, color: isDone ? T.success : T.textMuted }}>
                              {isDone ? '' : i + 1}
                            </div>
                            <span style={{ fontSize: 13, color: isDone ? T.success : T.textSecondary, flex: 1 }}>{p.name || p.description || `Fase ${i + 1}`}</span>
                            {p.estimated_tasks && <span style={{ fontSize: 11, color: T.textMuted }}>{p.estimated_tasks} tasks</span>}
                          </div>
                        );
                      })}
                      {pj.nextPhase > 0 && (
                        <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 8 }}>
                          {pj.nextPhase} de {phases.length} fases foram geradas antes do cancelamento.
                          {completedTasks.length > 0 && ` ${completedTasks.length} tarefas criadas.`}
                        </div>
                      )}
                    </div>
                  )}
                  {pj.tech_stack && pj.tech_stack.length > 0 && (
                    <div class="orch-card" style={{ borderLeft: `3px solid ${T.bgHover}` }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: T.textSecondary, marginBottom: 8 }}>Stack Tecnológico</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {pj.tech_stack.map((t: string, i: number) => (
                          <span key={i} style={{ fontSize: 12, color: T.text, background: T.accentBg, border: `1px solid ${T.accentBg}`, borderRadius: 6, padding: '4px 10px' }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Tasks list (hide during generation) */}
            {activePlan.status !== 'generating' && tasks.length > 0 && <div class="orch-card">
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span>Tarefas ({tasks.length})</span>
                {planMeta.complexity && (
                  <span class={`orch-complexity-badge ${planMeta.complexity}`}>
                    {planMeta.complexity === 'simple' ? 'Simples' : planMeta.complexity === 'medium' ? 'Médio' : 'Complexo'}
                  </span>
                )}
              </div>
              {(() => {
                // Group tasks by phase
                const phases = new Map<string, Task[]>();
                tasks.forEach(t => {
                  const phase = t.phase || '_none';
                  if (!phases.has(phase)) phases.set(phase, []);
                  phases.get(phase)!.push(t);
                });
                const hasPhases = phases.size > 1 || (phases.size === 1 && !phases.has('_none'));
                const allTasks = hasPhases ? Array.from(phases.entries()) : [['_none', tasks] as [string, Task[]]];
                let globalIdx = 0;
                return allTasks.map(([phase, phaseTasks]) => (
                  <div key={phase}>
                    {hasPhases && phase !== '_none' && (
                      <div class="orch-phase-header">
                        <span class="orch-phase-label">{phase}</span>
                        <div class="orch-phase-line" />
                        <span class="orch-phase-count">{phaseTasks.filter(t => t.status === 'completed').length}/{phaseTasks.length}</span>
                      </div>
                    )}
                    {phaseTasks.map(t => {
                      const i = globalIdx++;
              return (() => {
                const deps: string[] = JSON.parse(t.depends_on || '[]');
                const depNums = deps.map(d => `#${taskIdToNum.get(d) || '?'}`);
                const isExpanded = expandedTaskId === t.id;
                const execStep = (t as any).execution_step || 'pending';
                const sddStepsDef = [
                  { key: 'research', icon: '', label: 'Research', done: ['researched','speccing','specced','executing','executed','completed'] },
                  { key: 'spec', icon: '', label: 'Spec', done: ['specced','executing','executed','completed'] },
                  { key: 'execute', icon: '', label: 'Execute', done: ['executed','completed'] },
                  { key: 'done', icon: '', label: 'Done', done: ['completed'] },
                ];
                return (
                  <div key={t.id} style={{ marginBottom: 8 }}>
                    <div
                      class="orch-task-item"
                      data-status={t.status}
                      onClick={() => setExpandedTaskId(isExpanded ? null : t.id)}
                      style={{ cursor: 'pointer', marginBottom: 0 }}
                    >
                      <div class="orch-task-badge">
                        {t.status === 'completed' ? <span class="orch-checkmark">OK</span> : i + 1}
                      </div>
                      <div class="orch-task-info">
                        <div class="orch-task-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: T.textMuted, fontSize: 11, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}></span>
                          {t.title}
                          {/* SDD mini progress */}
                          {execStep !== 'pending' && (
                            <span style={{ display: 'inline-flex', gap: 2, marginLeft: 4 }}>
                              {sddStepsDef.map(s => <span key={s.key} style={{ fontSize: 10, opacity: s.done.includes(execStep) ? 1 : 0.2 }}>{s.icon}</span>)}
                            </span>
                          )}
                        </div>
                        <div class="orch-task-meta">
                          <span class="orch-task-type">{t.task_type}</span>
                          {t.assigned_agent_name && (
                            <span class="orch-task-agent" style={{ background: T.accentBg, color: T.purple }}>
                              {t.assigned_agent_name}
                            </span>
                          )}
                          {!t.assigned_agent_name && activePlan?.status === 'ready' && (
                            <span class="orch-task-agent" style={{ color: T.textMuted }}>Agente será atribuído na execução</span>
                          )}
                          <span class={`orch-status-pill ${t.status}`}>{execStep !== 'pending' ? execStep : t.status}</span>
                          {t.estimated_duration_min && <span style={{ color: T.textMuted }}>~{t.estimated_duration_min}min</span>}
                        </div>
                        {depNums.length > 0 && (
                          <div class="orch-task-deps">depende de: {depNums.join(', ')}</div>
                        )}
                      </div>
                    </div>
                    {/* Expanded: SDD sections */}
                    {isExpanded && (
                      <div style={{
                        padding: '12px 16px 12px 56px', background: T.bgSubtle,
                        borderLeft: `4px solid ${T.border}`, borderRadius: '0 0 8px 0',
                        animation: 'fadeInUp 0.2s ease',
                      }}>
                        {/* Task Brains */}
                        {t.brains && t.brains.length > 0 && (
                          <div class="orch-brains-section" style={{ marginBottom: 10 }}>
                            {t.brains.map(b => (
                              <span key={b.key} class="orch-brain-chip" style={{ color: b.color, borderColor: b.color + '33' }}>
                                {b.emoji} {b.label}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* SDD Progress bar */}
                        {execStep !== 'pending' && (
                          <div style={{ display: 'flex', gap: 10, marginBottom: 14, padding: '8px 12px', background: T.accentBg, borderRadius: 8 }}>
                            {sddStepsDef.map((s, si) => {
                              const active = s.done.includes(execStep);
                              const current = (execStep === 'researching' && s.key === 'research') || (execStep === 'speccing' && s.key === 'spec') || (execStep === 'executing' && s.key === 'execute');
                              return (
                                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span style={{ fontSize: 18, filter: active ? 'none' : 'grayscale(1)', opacity: active ? 1 : 0.3, animation: current ? 'pulse 1s infinite' : 'none' }}>{s.icon}</span>
                                  <span style={{ fontSize: 12, color: active ? T.text : T.textMuted, fontWeight: current ? 600 : 400 }}>{s.label}</span>
                                  {si < sddStepsDef.length - 1 && <span style={{ color: T.textMuted, margin: '0 6px' }}>→</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Descrição</div>
                        <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {t.description || 'Sem descrição'}
                        </div>
                        {/* Agent assignment — editable when ready */}
                        <div style={{ marginTop: 10, padding: '8px 12px', background: T.accentBg, borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 12, color: T.textSecondary, flexShrink: 0 }}>Agente:</span>
                          {activePlan?.status === 'ready' ? (
                            <select
                              value={t.assigned_runner_id || ''}
                              onChange={async (e: any) => {
                                const newId = e.target.value;
                                const newAgent = agents.find(a => a.id === newId);
                                try {
                                  await api.post(`/orchestrator/tasks/${t.id}/assign`, { runner_id: newId, agent_name: newAgent?.agent_name || 'Agent' });
                                  // Update local state
                                  const updatedTasks = tasks.map(tk => tk.id === t.id ? { ...tk, assigned_runner_id: newId, assigned_agent_name: newAgent?.agent_name || 'Agent' } : tk);
                                  setTasks(updatedTasks);
                                } catch { /* silent */ }
                              }}
                              style={{ flex: 1, height: 30, background: T.bgSecondary, border: `1px solid ${T.borderHover}`, borderRadius: 6, color: T.text, fontSize: 12, padding: '0 8px', outline: 'none', cursor: 'pointer', appearance: 'auto' }}
                            >
                              <option value="">Nenhum</option>
                              {agents.filter(a => a.status === 'online').map(a => <option key={a.id} value={a.id}>{a.agent_name}</option>)}
                              {agents.filter(a => a.status !== 'online').map(a => <option key={a.id} value={a.id}>{a.agent_name} (offline)</option>)}
                            </select>
                          ) : (
                            <span style={{ fontSize: 13, color: t.assigned_agent_name ? T.purple : T.textMuted, fontWeight: 500 }}>
                              {t.assigned_agent_name || 'Nao atribuido'}
                            </span>
                          )}
                        </div>
                        {/* SDD: Research output */}
                        {(t as any).research_output && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12, color: T.purple, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pesquisa</div>
                            <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap', background: T.purpleBg, padding: 12, borderRadius: 6, maxHeight: 250, overflow: 'auto', borderLeft: `3px solid ${T.purpleBg}` }}>
                              {(t as any).research_output}
                            </div>
                          </div>
                        )}
                        {/* SDD: Spec output */}
                        {(t as any).spec_output && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12, color: T.primary, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Especificação</div>
                            <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap', background: T.primaryBg, padding: 12, borderRadius: 6, maxHeight: 250, overflow: 'auto', borderLeft: `3px solid ${T.primaryBg}` }}>
                              {(t as any).spec_output}
                            </div>
                          </div>
                        )}
                        {/* Final output */}
                        {t.output && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12, color: T.success, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Resultado</div>
                            <div style={{
                              fontSize: 13, color: T.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                              background: T.successBg, padding: 12, borderRadius: 6, maxHeight: 300, overflow: 'auto', borderLeft: `3px solid ${T.successBg}`,
                            }}>
                              {t.output}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()})}
                  </div>
                ));
              })()}
            </div>}

            {/* ── BOTTOM ACTION BAR (ready state) ── */}
            {activePlan.status === 'ready' && (
              <div class="orch-card" style={{ animation: 'fadeInUp 0.3s ease' }}>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>Tudo pronto para executar</div>
                  <div style={{ fontSize: 12, color: T.textMuted }}>{tasks.length} tarefas serao processadas pelos agentes selecionados</div>
                </div>
                <Button variant="primary" onClick={executePlan} fullWidth style={{ padding: '14px 20px', fontSize: 15, borderRadius: 10 }}>
                  Iniciar Execucao
                </Button>
                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  <Button variant="ghost" onClick={() => { setActivePlan(null); setTasks([]); setEvents([]); setChannels([]); setChannelMessages([]); setExpandedTeamId(null); }} style={{ flex: 1, padding: '10px 16px', fontSize: 12 }}>Voltar</Button>
                  <Button variant="danger" onClick={cancelPlan} style={{ flex: 1, padding: '10px 16px', fontSize: 12 }}>Excluir plano</Button>
                </div>
              </div>
            )}

            {/* Timeline (only during/after execution, not on ready/generating) */}
            {events.length > 0 && activePlan.status !== 'ready' && activePlan.status !== 'generating' && (
              <div class="orch-card">
                <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12 }}>Timeline</div>
                <div class="orch-timeline" ref={timelineRef}>
                  {events.map((ev, i) => {
                    let detail = '';
                    try {
                      const d = JSON.parse(ev.event_data || '{}');
                      if (d.agent) detail = ` → ${d.agent}`;
                      if (d.reason) detail = ` — ${d.reason}`;
                      if (d.tasks) detail = ` (${d.tasks} tarefas)`;
                    } catch {}
                    return (
                      <div key={ev.id} class={`orch-timeline-item ${i === events.length - 1 ? 'flash' : ''}`}>
                        <span class="orch-timeline-ts">{formatTime(ev.created_at)}</span>
                        <span class="orch-timeline-text">{formatEventType(ev.event_type)}{detail}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Final output */}
            {activePlan.final_output && (
              <div class="orch-card" style={{ animation: 'fadeInUp 0.3s ease' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.success, marginBottom: 12 }}>Resultado Final</div>
                <div style={{ color: T.text, fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {activePlan.final_output}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
